import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiFetch, apiFetchBlob } from '@/lib/supabaseClient';
import { useEffectiveMe } from '@/lib/effectiveMe';
import { hasCap } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import NotificationsBell from '@/components/NotificationsBell';
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, AlertTriangle, Loader2,
  Calendar as CalendarIcon, Trash2, Pencil, Save, MoreHorizontal, Search,
  Menu as MenuIcon, Copy, Download, Share2, History, Undo2, RotateCcw,
  Check, CircleOff, Sparkles, RefreshCw, ShieldOff, UsersRound, StickyNote,
} from 'lucide-react';
import { toast } from 'sonner';
import turniVisualTokens from '@/data/turniVisualTokens.json';
import { isPureEmployee } from '@/lib/capabilities';
import MyAvailability from '@/components/MyAvailability';
import {
  buildPeriodResolveQuery, buildPublicationPayload, buildWeeklyDraftBatch,
} from './turniPeriodContract';

// ---------- helpers ----------
const WEEK_LABELS_FULL = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM'];
const MONTH_IT = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];

function startOfIsoWeek(d = new Date()) {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day);
  return x;
}
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const iso = (d) => {
  // Local-date ISO (YYYY-MM-DD) — no timezone drift. Business tz = Europe/Rome
  // but work_date must reflect the calendar day the user clicked on, regardless
  // of the browser tz. Using local components avoids off-by-one on UTC boundary.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const fmtHM = (t) => (t || '').slice(0, 5);
const dayNum = (d) => d.getDate();
const monthShort = (d) => MONTH_IT[d.getMonth()];
const weekRange = (start, end) =>
  `${dayNum(start)}–${dayNum(end)} ${monthShort(end)} ${end.getFullYear()}`;
function dateRangeContains(start, end, innerStart, innerEnd) {
  return Boolean(start && end && innerStart && innerEnd && start <= innerStart && end >= innerEnd);
}
const periodLabel = (start, end) => start && end
  ? `${dateLabelIT(start, { day: 'numeric', month: 'short', year: 'numeric' })} – ${dateLabelIT(end, { day: 'numeric', month: 'short', year: 'numeric' })}`
  : 'Periodo corrente';

function replacementErrorMessage(error, fallback) {
  const detail = error?.body?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  if (error?.status === 403) return 'Non hai il permesso per gestire le sostituzioni.';
  if (error?.status === 404) return 'Il turno non è più disponibile nello scope corrente.';
  if (error?.status === 409) return 'I dati del turno sono cambiati: aggiorna la griglia e riprova.';
  return error?.message || fallback;
}

const REPLACEMENT_PROXIMITY_LABELS = {
  SAME_LOCATION_DEPARTMENT: 'Stesso locale e reparto',
  SAME_COMPANY: 'Stessa azienda',
  SAME_TENANT: 'Altra azienda dello stesso tenant',
};
const REPLACEMENT_CANDIDATE_GROUPS = [
  {
    key: 'same-location-department',
    proximity: 'SAME_LOCATION_DEPARTMENT',
    title: 'Stesso locale e reparto',
    description: 'La scelta più vicina al turno originale',
    testId: 'same-location-department',
  },
  {
    key: 'same-company',
    proximity: 'SAME_COMPANY',
    title: 'Stessa azienda',
    description: 'Altri locali o reparti della stessa azienda',
    testId: 'same-company',
  },
  {
    key: 'same-tenant',
    proximity: 'SAME_TENANT',
    title: 'Altre aziende dello stesso tenant',
    description: 'Candidati autorizzati in altre aziende',
    testId: 'same-tenant',
  },
];
const REPLACEMENT_REASON_LABELS = {
  ORGANIZATIONAL_PROXIMITY: 'Vicinanza organizzativa',
  AVAILABILITY: 'Disponibilità',
  JOB_TITLE_MATCH: 'Mansione compatibile',
  LOWER_WEEKLY_LOAD: 'Carico settimanale',
};

function replacementProximityLabel(value) {
  return REPLACEMENT_PROXIMITY_LABELS[value] || value || '—';
}

function groupReplacementCandidates(candidates) {
  return REPLACEMENT_CANDIDATE_GROUPS.map(group => ({
    ...group,
    candidates: candidates.filter(candidate => (
      group.proximity === 'SAME_TENANT'
        ? candidate.organizational_proximity !== 'SAME_LOCATION_DEPARTMENT'
          && candidate.organizational_proximity !== 'SAME_COMPANY'
        : candidate.organizational_proximity === group.proximity
    )),
  })).filter(group => group.candidates.length > 0);
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => (
    typeof window === 'undefined'
      || !window.matchMedia
      || window.matchMedia('(min-width: 768px)').matches
  ));

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mediaQuery.matches);
    update();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  return isDesktop;
}

function hoursOf(shift) {
  if (!hasShiftTimeRange(shift)) return 0;
  const [sh, sm] = fmtHM(shift.start_time).split(':').map(Number);
  const [eh, em] = fmtHM(shift.end_time).split(':').map(Number);
  const h = (eh + em / 60) - (sh + sm / 60) - (shift.break_minutes || 0) / 60;
  return Math.max(0, h);
}

const TIMELESS_SHIFT_TYPES = new Set([
  'RIPOSO',
  'FERIE',
  'MALATTIA',
  'PERMESSO',
  'ASSENZA_GIUSTIFICATA',
  'ASSENZA_NON_GIUSTIFICATA',
  'NON_DISPONIBILE',
]);

function shiftHasTimeFields(shiftType) {
  return !TIMELESS_SHIFT_TYPES.has(shiftType || 'LAVORO');
}

function normalizeShiftTimeFields(shift) {
  if (!shiftHasTimeFields(shift?.shift_type)) {
    return {
      ...shift,
      start_time: null,
      end_time: null,
      break_minutes: 0,
    };
  }
  return {
    ...shift,
    start_time: shift?.start_time || null,
    end_time: shift?.end_time || null,
    break_minutes: Number(shift?.break_minutes) || 0,
  };
}

function hasShiftTimeRange(shift) {
  const isRestShift = shift?.shift_type === 'RIPOSO';
  return Boolean(
    shift
      && shiftHasTimeFields(shift.shift_type)
      && !isRestShift
      && fmtHM(shift.start_time)
      && fmtHM(shift.end_time),
  );
}

// Status resolution: for each (employee_id, date, service_id) determine
// the visual state to show — real data only, no invented values.
// Priority: CANCELLED shift → Annullato, ACTIVE shift → time chip,
// FERIE_APPROVATA warning → Ferie, NON_DISPONIBILE avail → Non disp.
const SHIFT_TYPES = Object.entries(turniVisualTokens)
  .map(([value, meta]) => [value, meta.label, meta]);
const SHIFT_TYPE_META = Object.fromEntries(
  SHIFT_TYPES.map(([value, label, meta]) => [value, { label, ...meta }]),
);
const QUICK_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? '00' : '30';
  return `${String(hours).padStart(2, '0')}:${minutes}`;
});
const QUICK_BREAK_OPTIONS = [0, 15, 30, 45, 60, 90, 120];

function QuickTimeField({ id, testId, label, value, onChange, disabled }) {
  return (
    <div className="min-w-0">
      <Label className="text-xs" htmlFor={id}>{label}</Label>
      <Input id={id} type="time" step="60" list={`${id}-options`} data-testid={testId}
        value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      <select aria-label={`${label} selezione rapida`} data-testid={`${testId}-quick`}
        value="" disabled={disabled} onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
        className="mt-1 h-8 w-full rounded-md border border-stone-200 bg-white px-1.5 text-[11px] text-stone-600">
        <option value="">Scelta rapida</option>
        {QUICK_TIME_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
      </select>
      <datalist id={`${id}-options`}>
        {QUICK_TIME_OPTIONS.map(option => <option key={option} value={option} />)}
      </datalist>
    </div>
  );
}

function QuickBreakField({ id, testId, label, value, onChange, disabled }) {
  return (
    <div className="min-w-0">
      <Label className="text-xs" htmlFor={id}>{label}</Label>
      <Input id={id} type="number" min="0" step="1" data-testid={testId}
        value={value ?? ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      <select aria-label={`${label} selezione rapida`} data-testid={`${testId}-quick`}
        value="" disabled={disabled} onChange={(e) => { if (e.target.value !== '') onChange(e.target.value); }}
        className="mt-1 h-8 w-full rounded-md border border-stone-200 bg-white px-1.5 text-[11px] text-stone-600">
        <option value="">Scelta rapida</option>
        {QUICK_BREAK_OPTIONS.map(option => <option key={option} value={option}>{option} min</option>)}
      </select>
    </div>
  );
}

// ---------- main page ----------
export default function Turni() {
  const me = useEffectiveMe();
  if (isPureEmployee(me)) return <MyAvailability />;
  return <ManagerTurni />;
}

// ---------- employee self-service ----------
const AVAILABILITY_STATES = [
  {
    value: 'DISPONIBILE',
    label: 'Disponibile',
    shortLabel: 'Disponibile',
    icon: Check,
    buttonClass: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100',
    activeClass: 'border-emerald-500 bg-emerald-100 text-emerald-800 ring-2 ring-emerald-200',
    badgeClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  },
  {
    value: 'NON_DISPONIBILE',
    label: 'Non disponibile',
    shortLabel: 'Non disp.',
    icon: CircleOff,
    buttonClass: 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100',
    activeClass: 'border-red-500 bg-red-100 text-red-800 ring-2 ring-red-200',
    badgeClass: 'bg-red-50 text-red-800 border-red-200',
  },
  {
    value: 'PREFERENZA',
    label: 'Preferenza',
    shortLabel: 'Preferenza',
    icon: Sparkles,
    buttonClass: 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100',
    activeClass: 'border-amber-500 bg-amber-100 text-amber-800 ring-2 ring-amber-200',
    badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
  },
];

function localDateValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function availabilityStateForDate(items, date) {
  const matches = items.filter(item => item.shift_date === date);
  const wholeDay = matches.find(item => !item.service_id);
  if (wholeDay) return wholeDay.state;
  const states = [...new Set(matches.map(item => item.state))];
  return states.length === 1 ? states[0] : states.length > 1 ? 'MIXED' : null;
}

function availabilityMeta(state) {
  return AVAILABILITY_STATES.find(item => item.value === state) || null;
}

function availabilityStateLabel(state) {
  return {
    DISPONIBILE: 'Disponibile',
    DISPONIBILE_SE_SERVE: 'Disponibile se serve',
    PREFERISCO_LAVORARE: 'Preferisco lavorare',
    PREFERENZA: 'Preferenza',
    INDISPONIBILE: 'Non disponibile',
    NON_DISPONIBILE: 'Non disponibile',
  }[state] || 'Non indicata';
}

function localDateFromValue(value) {
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function dateLabelIT(value, options = { weekday: 'long', day: 'numeric', month: 'long' }) {
  return localDateFromValue(value).toLocaleDateString('it-IT', options);
}

function monthBounds(viewDate) {
  return {
    fromDate: localDateValue(new Date(viewDate.getFullYear(), viewDate.getMonth(), 1)),
    toDate: localDateValue(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0)),
  };
}

function monthDays(viewDate) {
  const { fromDate, toDate } = monthBounds(viewDate);
  const days = [];
  for (let day = localDateFromValue(fromDate); localDateValue(day) <= toDate; day = addDays(day, 1)) {
    days.push(day);
  }
  return days;
}

function MyShiftsPage() {
  const me = useEffectiveMe();
  const outletCtx = useOutletContext() || {};
  const openMobileMenu = outletCtx.openMobileMenu || (() => {});
  const [viewDate, setViewDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const canViewShifts = hasCap(me, 'turni.view');
  const canManageAvailability = hasCap(me, 'availability.self.manage');
  const { fromDate, toDate } = monthBounds(viewDate);
  const monthLabel = viewDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  const loadShifts = useCallback(async () => {
    if (!canViewShifts) {
      setLoading(false);
      setShifts([]);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const shiftData = await apiFetch(`/api/turni/my-shifts?from_date=${fromDate}&to_date=${toDate}`);
      setShifts(Array.isArray(shiftData) ? shiftData : []);
    } catch (error) {
      setLoadError(error.message || 'Non è stato possibile caricare i tuoi turni.');
    } finally {
      setLoading(false);
    }
  }, [canViewShifts, fromDate, toDate]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const changeMonth = (offset) => {
    setViewDate(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const goToToday = () => {
    const today = new Date();
    setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const shiftsByDate = useMemo(() => shifts.reduce((groups, shift) => {
    const date = shift.shift_date;
    groups[date] = groups[date] || [];
    groups[date].push(shift);
    return groups;
  }, {}), [shifts]);

  const serviceName = (shift) => (
    shift.service?.name
      || shift.service?.code
      || 'Servizio'
  );

  return (
    <div className="flex min-h-[calc(100vh-0px)] flex-col bg-app-background pb-[76px] md:space-y-3 md:pb-0"
      data-testid="my-shifts-page">
      <header className="ros-mobile-topbar md:hidden sticky top-0 z-30"
        data-testid="my-shifts-mobile-topbar">
        <div className="flex items-center gap-3 px-3 py-2 min-h-[60px]">
          <button onClick={openMobileMenu} data-testid="my-shifts-mobile-menu-btn"
            className="w-10 h-10 -ml-1 flex items-center justify-center rounded-xl text-stone-600 hover:bg-stone-100 border border-stone-200"
            aria-label="Apri menu">
            <MenuIcon className="w-5 h-5" />
          </button>
          <img src="/brand/restaurantos-logo-symbol.svg" alt="" className="w-8 h-8" />
          <div className="flex-1 min-w-0 leading-none">
            <div className="text-[11px] font-semibold text-stone-500 tracking-tight mb-1">RestaurantOS</div>
            <div className="font-display font-bold text-brand-navy text-xl tracking-tight">I miei turni</div>
          </div>
          <NotificationsBell />
        </div>
      </header>

      <div className="flex-1 min-w-0 flex flex-col gap-4 px-3 pt-4 md:px-0 md:pt-0 md:gap-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Area personale</div>
            <h1 className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-brand-navy flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 md:w-7 md:h-7 text-brand-green" />
              I miei turni
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              Qui trovi solo i turni pubblicati per te. Puoi comunicare la tua disponibilità quando vuoi.
            </p>
          </div>
          {canManageAvailability && (
            <button type="button" onClick={() => setAvailabilityOpen(true)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-green-hover"
              data-testid="my-shifts-set-availability">
              <CalendarIcon className="h-4 w-4" /> Imposta disponibilità
            </button>
          )}
        </div>

        {!canViewShifts && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            role="status" data-testid="my-shifts-not-authorized">
            La consultazione dei turni non è abilitata per il tuo profilo.
          </div>
        )}

        {loadError && (
          <div className="flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 sm:flex-row sm:items-center sm:justify-between"
            role="alert" data-testid="my-shifts-error">
            <div className="flex items-start gap-2">
              <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{loadError}</span>
            </div>
            <button type="button" onClick={loadShifts}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-3 font-semibold text-red-800 hover:bg-red-100"
              data-testid="my-shifts-retry">
              <RefreshCw className="h-3.5 w-3.5" /> Riprova
            </button>
          </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm"
          aria-labelledby="my-shifts-month-title" data-testid="my-shifts-summary">
          <div className="flex flex-wrap items-center gap-2 border-b border-stone-100 px-3 py-3 md:px-5">
            <button type="button" onClick={() => changeMonth(-1)} disabled={loading}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Mese precedente" data-testid="my-shifts-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <h2 id="my-shifts-month-title"
              className="min-w-[150px] flex-1 text-center font-display text-lg font-semibold capitalize text-brand-navy md:text-xl">
              {monthLabel}
            </h2>
            <button type="button" onClick={goToToday} disabled={loading}
              className="h-9 rounded-xl border border-stone-200 px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="my-shifts-today">
              Oggi
            </button>
            <button type="button" onClick={() => changeMonth(1)} disabled={loading}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Mese successivo" data-testid="my-shifts-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {loading ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 px-4 py-12 text-sm text-stone-500"
              role="status" aria-live="polite" data-testid="my-shifts-loading">
              <Loader2 className="h-7 w-7 animate-spin text-brand-green" />
              <span>Caricamento dei tuoi turni…</span>
            </div>
          ) : !canViewShifts ? null : shifts.length === 0 ? (
            <div className="px-4 py-12 text-center" role="status" data-testid="my-shifts-empty">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-stone-100 text-stone-400">
                <CalendarIcon className="h-6 w-6" />
              </div>
              <p className="mt-3 font-semibold text-brand-navy">Nessun turno pubblicato</p>
              <p className="mx-auto mt-1 max-w-md text-sm text-stone-500">
                Per {monthLabel} non risultano ancora turni pubblicati a tuo nome.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100" data-testid="my-shifts-list">
              {Object.entries(shiftsByDate).map(([date, dateShifts]) => (
                <div key={date} className="p-4 md:px-5" data-testid={`my-shifts-date-${date}`}>
                  <div className="mb-3 flex items-center gap-2">
                    <time dateTime={date} className="font-display text-base font-semibold capitalize text-brand-navy">
                      {dateLabelIT(date)}
                    </time>
                    <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">
                      {dateShifts.length} {dateShifts.length === 1 ? 'turno' : 'turni'}
                    </span>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {dateShifts.map(shift => (
                      <div key={shift.shift_id} className="rounded-xl border border-stone-200 bg-stone-50/60 p-3"
                        data-testid={`my-shift-${shift.shift_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-brand-navy">{serviceName(shift)}</div>
                            <div className="mt-1 text-lg font-display font-semibold text-stone-800">
                              {fmtHM(shift.start_time)} <span className="text-stone-400">–</span> {fmtHM(shift.end_time)}
                            </div>
                          </div>
                          <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-bold text-emerald-800">
                            Pubblicato
                          </span>
                        </div>
                         {String(shift.notes || '').trim() && (
                           <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
                             data-testid={`my-shift-note-${shift.shift_id}`}>
                             <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                             <span className="whitespace-pre-wrap break-words">{String(shift.notes).trim()}</span>
                           </div>
                         )}
                         <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500">
                          <span>{shift.shift_type === 'SOSTITUZIONE' ? 'Sostituzione' : 'Turno di lavoro'}</span>
                          {shift.break_minutes > 0 && <span>Pausa {shift.break_minutes} min</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <p className="pb-2 text-center text-xs text-stone-500">Periodo visualizzato: {fromDate} – {toDate}</p>
      </div>
      {canManageAvailability && (
        <AvailabilitySheet open={availabilityOpen} onOpenChange={setAvailabilityOpen} initialDate={viewDate} />
      )}
    </div>
  );
}

function AvailabilitySheet({ open, onOpenChange, initialDate }) {
  const [viewDate, setViewDate] = useState(initialDate);
  const [availability, setAvailability] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const days = useMemo(() => monthDays(viewDate), [viewDate]);
  const { fromDate, toDate } = monthBounds(viewDate);
  const hasDraft = Object.keys(draft).length > 0;

  useEffect(() => {
    if (open) setViewDate(initialDate);
  }, [open, initialDate]);

  const loadAvailability = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    setSaved(false);
    try {
      const data = await apiFetch(`/api/turni/my-availability?from_date=${fromDate}&to_date=${toDate}`);
      setAvailability(Array.isArray(data) ? data : []);
      setDraft({});
    } catch (error) {
      setLoadError(error.message || 'Non è stato possibile caricare la disponibilità.');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    if (open) loadAvailability();
  }, [open, loadAvailability]);

  const stateForDate = (date) => (
    Object.prototype.hasOwnProperty.call(draft, date)
      ? draft[date]
      : availabilityStateForDate(availability, date)
  );

  const selectState = (date, state) => {
    setSaved(false);
    setSaveError('');
    setDraft(current => ({ ...current, [date]: state }));
  };

  const changeMonth = (offset) => {
    if (hasDraft) {
      toast.error('Salva o annulla le modifiche prima di cambiare mese.');
      return;
    }
    setViewDate(current => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  const saveAvailability = async () => {
    const items = Object.entries(draft).map(([shift_date, state]) => ({
      shift_date,
      service_id: null,
      state,
      notes: null,
    }));
    if (!items.length) return;
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      await apiFetch('/api/turni/my-availability', {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
      setAvailability(current => {
        const changed = new Set(Object.keys(draft));
        return [
          ...current.filter(item => !changed.has(item.shift_date)),
          ...items.filter(item => item.state !== null),
        ];
      });
      setDraft({});
      setSaved(true);
      toast.success('Disponibilità aggiornata');
    } catch (error) {
      setSaveError(error.message || 'Non è stato possibile salvare la disponibilità.');
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setDraft({});
    setSaveError('');
    setSaved(false);
    toast.success('Modifiche annullate');
  };

  const closeSheet = (nextOpen) => {
    if (!nextOpen && hasDraft && !saving) {
      setDraft({});
      setSaveError('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Sheet open={open} onOpenChange={closeSheet}>
      <SheetContent side="bottom"
        className="flex max-h-[92vh] flex-col rounded-t-3xl p-0 sm:inset-y-0 sm:bottom-auto sm:left-auto sm:right-0 sm:h-full sm:w-full sm:max-w-xl sm:rounded-none sm:border-l sm:border-t-0 sm:slide-in-from-right"
        data-testid="my-availability-sheet">
        <SheetHeader className="border-b border-stone-100 px-4 pb-4 pt-5 text-left md:px-6">
          <SheetTitle className="font-display text-xl text-brand-navy">Imposta disponibilità</SheetTitle>
          <SheetDescription className="text-xs text-stone-500">
            Scegli uno stato per ogni giornata. Le modifiche restano locali fino a “Salva”.
          </SheetDescription>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b border-stone-100 px-4 py-3 md:px-6">
            <button type="button" onClick={() => changeMonth(-1)} disabled={loading || saving}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40"
              aria-label="Mese precedente" data-testid="my-availability-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex-1 text-center font-display font-semibold capitalize text-brand-navy">
              {viewDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
            </div>
            <button type="button" onClick={() => changeMonth(1)} disabled={loading || saving}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-stone-200 text-stone-600 hover:bg-stone-50 disabled:opacity-40"
              aria-label="Mese successivo" data-testid="my-availability-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <AvailabilityLegend />
          {loadError && (
            <div className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900 md:mx-6"
              role="alert" data-testid="my-availability-error">
              <span>{loadError}</span>
              <button type="button" onClick={loadAvailability} className="shrink-0 font-bold underline" data-testid="my-availability-retry">
                Riprova
              </button>
            </div>
          )}
          {saveError && (
            <div className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-900 md:mx-6"
              role="alert" data-testid="my-availability-save-error">
              <span>{saveError}</span>
              <button type="button" onClick={saveAvailability} disabled={saving} className="shrink-0 font-bold underline disabled:opacity-50"
                data-testid="my-availability-save-retry">Riprova</button>
            </div>
          )}
          {loading ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-4 py-12 text-sm text-stone-500"
              role="status" aria-live="polite" data-testid="my-availability-loading">
              <Loader2 className="h-7 w-7 animate-spin text-brand-green" />
              Caricamento della tua disponibilità…
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2 md:px-6" data-testid="my-availability-list">
              {days.map(day => {
                const date = localDateValue(day);
                const state = stateForDate(date);
                const meta = availabilityMeta(state);
                const label = dateLabelIT(date);
                return (
                  <div key={date} className="border-b border-stone-100 py-3 last:border-b-0" data-testid={`my-availability-day-${date}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <time dateTime={date} className="text-sm font-semibold capitalize text-brand-navy">{label}</time>
                      <span className={`text-[11px] font-medium ${meta ? 'text-stone-600' : 'text-stone-400'}`}>
                        {meta?.label || (state === 'MIXED' ? 'Più stati' : 'Non indicata')}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5" role="group" aria-label={`Disponibilità per ${label}`}>
                      {AVAILABILITY_STATES.map(option => {
                        const Icon = option.icon;
                        const selected = state === option.value;
                        return (
                          <button type="button" key={option.value} onClick={() => selectState(date, option.value)}
                            aria-label={`${option.label} per ${label}`} aria-pressed={selected}
                            className={`flex min-h-10 items-center justify-center gap-1 rounded-lg border px-1 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green ${
                              selected ? option.activeClass : option.buttonClass
                            }`} data-testid={`my-availability-${date}-${option.value.toLowerCase()}`}>
                            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="hidden min-[390px]:inline">{option.shortLabel}</span>
                          </button>
                        );
                      })}
                      <button type="button" onClick={() => selectState(date, null)}
                        aria-label={`Non indicata per ${label}`} aria-pressed={state === null}
                        className={`flex min-h-10 items-center justify-center gap-1 rounded-lg border px-1 text-[10px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green ${
                          state === null ? 'border-stone-400 bg-stone-100 text-stone-700 ring-2 ring-stone-200' : 'border-stone-200 bg-white text-stone-500 hover:bg-stone-50'
                        }`} data-testid={`my-availability-${date}-reset`}>
                        <CircleOff className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="hidden min-[390px]:inline">Non indicata</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border-t border-stone-100 bg-white px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 md:px-6 md:pb-4">
          {saved && <p className="mb-2 text-xs font-semibold text-emerald-700" role="status" data-testid="my-availability-saved">Disponibilità salvata.</p>}
          {hasDraft && <p className="mb-2 text-xs text-stone-500">{Object.keys(draft).length} {Object.keys(draft).length === 1 ? 'modifica pronta' : 'modifiche pronte'} da salvare</p>}
          <div className="flex gap-2">
            <button type="button" onClick={discardChanges} disabled={!hasDraft || saving}
              className="h-11 flex-1 rounded-xl border border-stone-200 bg-white px-3 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-50"
              data-testid="my-availability-discard">Annulla</button>
            <button type="button" onClick={saveAvailability} disabled={!hasDraft || saving || loading}
              className="inline-flex h-11 flex-[1.5] items-center justify-center gap-2 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white hover:bg-brand-green-hover disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="my-availability-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Salvataggio…' : 'Salva disponibilità'}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AvailabilityLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-3 text-[11px] text-stone-600 md:px-5"
      aria-label="Legenda disponibilità" data-testid="turni-availability-legend">
      <span className="mr-1 font-bold uppercase tracking-wider text-stone-400">Legenda</span>
      {AVAILABILITY_STATES.map(option => {
        const Icon = option.icon;
        return (
          <span key={option.value} className="inline-flex items-center gap-1.5">
            <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${option.badgeClass}`}>
              <Icon className="h-3 w-3" aria-hidden="true" />
            </span>
            {option.label}
          </span>
        );
      })}
      <span className="inline-flex items-center gap-1.5 text-stone-400">
        <span className="h-2 w-2 rounded-full bg-stone-300" aria-hidden="true" /> Non indicata
      </span>
    </div>
  );
}

function ManagerTurni() {
  const me = useEffectiveMe();
  const outletCtx = useOutletContext() || {};
  const openMobileMenu = outletCtx.openMobileMenu || (() => {});
  const canView    = hasCap(me, 'turni.view');
  const canCreate  = hasCap(me, 'turni.create');
  const canEdit    = hasCap(me, 'turni.edit');
  const canPublish = hasCap(me, 'turni.publish');
  const canReplace = hasCap(me, 'turni.replace');
  const isDesktop = useIsDesktop();

  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [scopeOpen, setScopeOpen] = useState(false);

  const [weekStart, setWeekStart] = useState(startOfIsoWeek());
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const [period, setPeriod] = useState(null);
  const [ws, setWs] = useState(null);
  const [viewedVersion, setViewedVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shiftModal, setShiftModal] = useState(null);
  const [periodModal, setPeriodModal] = useState(null);
  const [replacementModal, setReplacementModal] = useState(null);
  const [versions, setVersions] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [editingWeek, setEditingWeek] = useState(false);
  const [weekSnapshot, setWeekSnapshot] = useState(null);
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);
  const [periodConflict, setPeriodConflict] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showCancelled, setShowCancelled] = useState(false);
  const [warningDialog, setWarningDialog] = useState(null);
  const [publicationConfirm, setPublicationConfirm] = useState(null);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const draftWarningAcknowledgementsRef = useRef(new Map());
  const availabilityDesktopSummaryRef = useRef(null);
  const availabilityMobileSummaryRef = useRef(null);
  const turniGridRef = useRef(null);
  const requestSequence = useRef(0);
  const pdfBlobCacheRef = useRef(new Map());

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const [c, l] = await Promise.all([apiFetch('/api/companies'), apiFetch('/api/locations')]);
        setCompanies(c || []); setLocations(l || []);
        if (c?.length === 1) setCompanyId(c[0].id);
      } catch (e) { toast.error(e.message || 'Errore scope'); }
    })();
  }, [canView]);

  useEffect(() => {
    if (!locationId) { setDepartments([]); return; }
    apiFetch(`/api/departments?location_id=${locationId}`).then(setDepartments).catch(() => setDepartments([]));
  }, [locationId]);

  const locationsInCompany = useMemo(
    () => locations.filter(l => !companyId || l.company_id === companyId), [locations, companyId]);

  const company = companies.find(c => c.id === companyId);
  const location = locations.find(l => l.id === locationId);
  const department = departments.find(d => d.id === departmentId);
  const scopeLabel = [company?.name, location?.name, department?.name || 'Tutti i reparti'].filter(Boolean).join(' · ') || 'Seleziona scope';

  const loadPeriodAndWorkspace = useCallback(async () => {
    const requestId = ++requestSequence.current;
    const isCurrent = () => requestId === requestSequence.current;
    if (!companyId || !locationId) {
      setPeriod(null);
      setWs(null);
      setPeriodConflict(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setPeriod(null);
    setWs(null);
    try {
      setPeriodConflict(null);
      const params = buildPeriodResolveQuery({
        companyId, locationId, departmentId,
        startDate: iso(weekStart), endDate: iso(weekEnd),
      });
      const resolved = await apiFetch(`/api/turni/periods/resolve?${params}`);
      if (!isCurrent()) return;
      const match = resolved?.period || null;
      if (match) {
        setPeriod(match);
        const versionQuery = viewedVersion ? `&version=${viewedVersion}` : '';
        const w = await apiFetch(
          `/api/turni/periods/${match.id}/workspace?view_start=${iso(weekStart)}&view_end=${iso(weekEnd)}${versionQuery}`,
        );
        if (!isCurrent()) return;
        setWs(w);
        return { period: match, workspace: w };
        } else {
        setPeriod(null); setWs(null);
        if (resolved?.conflicts?.length) {
          const conflict = resolved.conflicts[0];
          setPeriodConflict(conflict?.existing_period || conflict);
        }
      }
    } catch (e) {
      if (!isCurrent()) return;
      toast.error(e.message || 'Errore caricamento'); setPeriod(null); setWs(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [companyId, locationId, departmentId, weekStart, weekEnd, viewedVersion]);
  useEffect(() => { loadPeriodAndWorkspace(); }, [loadPeriodAndWorkspace]);
  const loadVersionHistory = useCallback(async (periodId = period?.id) => {
    if (!periodId) {
      setVersions(null);
      setVersionsOpen(false);
      return [];
    }
    try {
      const history = await apiFetch(`/api/turni/periods/${periodId}/versions`);
      setVersions(Array.isArray(history) ? history : []);
      return history;
    } catch (e) {
      toast.error(e.message || 'Errore caricamento versioni');
      return [];
    }
  }, [period?.id]);
  useEffect(() => {
    if (period?.id) loadVersionHistory(period.id);
    else {
      setVersions(null);
      setVersionsOpen(false);
    }
  }, [period?.id, loadVersionHistory]);
  useEffect(() => {
    setEditingWeek(false);
    setWeekSnapshot(null);
    setDraftSaveFailed(false);
    setViewedVersion(null);
    setVersionsOpen(false);
    setShiftModal(null);
    setReplacementModal(null);
    setAvailabilityOpen(false);
  }, [weekStart, locationId, departmentId]);

  const createPeriod = async () => {
    if (!companyId || !locationId || period) return;
    setActionBusy('create');
    try {
      await apiFetch('/api/turni/periods', {
        method: 'POST',
        body: JSON.stringify({
          company_id: companyId, location_id: locationId,
          department_id: departmentId || undefined,
          period_type: 'WEEK', start_date: iso(weekStart), end_date: iso(weekEnd),
        }),
      });
      toast.success('Periodo creato');
      await loadPeriodAndWorkspace();
    } catch (e) {
      const detail = e?.body?.detail;
      if (e?.status === 409 && detail?.code === 'PERIOD_OVERLAP') {
        const existing = detail.existing_period;
        // A concurrent create may have won the race. Resolve once on the
        // current view, then retain the explicit conflict when the backend
        // supplied a period that can be opened.
        await loadPeriodAndWorkspace();
        if (existing) {
          setPeriod(null);
          setWs(null);
          setPeriodConflict(existing);
        }
        else setPeriodConflict({ message: 'Il periodo richiesto è sovrapposto a un altro periodo.' });
      } else toast.error(e.message || 'Errore creazione');
    }
    finally { setActionBusy(''); }
  };
  const reloadWorkspace = async () => {
    if (!period) return;
    await loadPeriodAndWorkspace();
  };

  const warningDetail = (error) => {
    const detail = error?.body?.detail;
    return detail?.code === 'WARNINGS_REQUIRE_CONFIRMATION' ? detail : null;
  };
  const warningContexts = (items = []) => items.map(item => {
    const employee = employees.find(candidate => candidate.employee_id === item.employee_id);
    const service = services.find(candidate => candidate.id === item.service_id);
    const declared = (ws?.availability || []).find(candidate =>
      candidate.employee_id === item.employee_id
      && candidate.shift_date === item.shift_date
      && (candidate.service_id === item.service_id || !candidate.service_id));
    const declaredLabel = declared
      ? `${availabilityStateLabel(declared.state)}${declared.start_time && declared.end_time
        ? ` · ${fmtHM(declared.start_time)}–${fmtHM(declared.end_time)}` : ''}`
      : 'Nessuna disponibilità dichiarata';
    return {
      id: item.id,
      employeeName: employee?.display_name || item.employee_name || 'Dipendente',
      date: item.shift_date,
      service: service?.name || item.service_name || 'Servizio',
      shift: item.start_time && item.end_time ? `${fmtHM(item.start_time)}–${fmtHM(item.end_time)}` : '—',
      availability: declaredLabel,
    };
  });
  const draftWarningKey = (item) => [
    item.employee_id, item.shift_date, item.service_id, item.start_time, item.end_time,
  ].join('::');
  const warningMatchesDraftItem = (warning, item) => {
    const metadata = warning.metadata || {};
    return (!metadata.employee_id || String(metadata.employee_id) === String(item.employee_id))
      && (!metadata.date || metadata.date === item.shift_date)
      && (!metadata.service_id || String(metadata.service_id) === String(item.service_id));
  };
  const rememberDraftWarningAcknowledgements = (warnings, items, reason) => {
    warnings.filter(warning => warning.severity === 'warning').forEach(warning => {
      items.filter(item => warningMatchesDraftItem(warning, item)).forEach(item => {
        draftWarningAcknowledgementsRef.current.set(
          `${warning.code}::${draftWarningKey(item)}`, reason || null,
        );
      });
    });
  };
  const isDraftWarningAcknowledged = (warning, items) => items.some(item =>
    warningMatchesDraftItem(warning, item)
      && draftWarningAcknowledgementsRef.current.has(`${warning.code}::${draftWarningKey(item)}`));
  const draftWarningReason = (warning, items) => {
    const item = items.find(candidate => warningMatchesDraftItem(warning, candidate));
    return item
      ? draftWarningAcknowledgementsRef.current.get(`${warning.code}::${draftWarningKey(item)}`)
      : null;
  };
  const preflightWarnings = async (operation, items) => {
    const result = await apiFetch(`/api/turni/periods/${period.id}/warnings/preflight`, {
      method: 'POST',
      body: JSON.stringify({ operation, items }),
    });
    return result?.warnings || [];
  };
  const runWithWarningConfirmation = async (warnings, execute, context = []) => {
    if (!warnings?.length) return execute(false, null);
    return new Promise(resolve => {
      setWarningDialog({
        warnings,
        context,
        onCancel: () => { setWarningDialog(null); resolve(null); },
        onConfirm: async (reason) => {
          setWarningDialog(null);
          try {
            resolve(await execute(true, reason));
          } catch (error) {
            resolve(Promise.reject(error));
          }
        },
      });
    });
  };
  const requestWithWarningRetry = async (execute, context = []) => {
    try {
      return await execute(false, null);
    } catch (error) {
      const detail = warningDetail(error);
      if (!detail) throw error;
      return runWithWarningConfirmation(detail.warnings || [], (confirmed, reason) =>
        execute(confirmed, reason), context);
    }
  };

  // ---- lookups ----
  const services = useMemo(() => ws?.services || [], [ws]);
  const gridShifts = useMemo(
    () => (ws?.shifts || []).filter(s =>
      s.shift_date >= iso(weekStart) && s.shift_date <= iso(weekEnd)
      && (showCancelled || s.status !== 'CANCELLED')),
    [ws, showCancelled, weekStart, weekEnd],
  );
  const shiftsByCell = useMemo(() => {
    const m = {};
    gridShifts.forEach(s => {
      const k = `${s.employee_id}::${s.shift_date}::${s.service_id}`;
      (m[k] = m[k] || []).push(s);
    });
    return m;
  }, [gridShifts]);
  const weeklyHours = useMemo(() => {
    const m = {}; gridShifts.filter(s => s.status === 'ACTIVE' && hasShiftTimeRange(s)).forEach(s => {
      m[s.employee_id] = (m[s.employee_id] || 0) + hoursOf(s);
    }); return m;
  }, [gridShifts]);
  const coverageByCell = useMemo(() => {
    const m = {}; (ws?.coverage || []).forEach(c => { m[`${c.date}::${c.service_id}`] = c; }); return m;
  }, [ws]);
  const employees = useMemo(() => {
    const by = {};
    (ws?.eligible_employees || []).forEach(e => {
      if (!by[e.employee_id]) by[e.employee_id] = { ...e, assignments: [] };
      by[e.employee_id].assignments.push({
        assignment_id: e.assignment_id, department_id: e.department_id,
        department_name: e.department_name,
        mansione: e.mansione, is_primary: e.is_primary,
      });
    });
    return Object.values(by).sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
  }, [ws]);
  const visibleEmployees = useMemo(() => {
    const q = employeeSearch.trim().toLocaleLowerCase('it-IT');
    if (!q) return employees;
    return employees.filter(e =>
      [e.display_name, e.mansione, e.department_name].filter(Boolean)
        .some(value => value.toLocaleLowerCase('it-IT').includes(q))
    );
  }, [employees, employeeSearch]);
  const availabilityRows = useMemo(() => {
    const employeeById = Object.fromEntries(employees.map(employee => [employee.employee_id, employee]));
    const serviceById = Object.fromEntries(services.map(service => [service.id, service]));
    return (ws?.availability || []).filter(item =>
      item.shift_date >= iso(weekStart) && item.shift_date <= iso(weekEnd)
    ).map(item => ({
      ...item,
      employee_name: employeeById[item.employee_id]?.display_name || 'Dipendente',
      service_name: serviceById[item.service_id]?.name
        || (item.service_id ? 'Servizio' : 'Tutta la giornata'),
    }));
  }, [employees, services, ws, weekStart, weekEnd]);
  const periodNotes = useMemo(() => {
    const employeeById = Object.fromEntries(employees.map(employee => [employee.employee_id, employee]));
    const serviceById = Object.fromEntries(services.map(service => [service.id, service]));
    return gridShifts
      .filter(shift => shift.status === 'ACTIVE' && String(shift.notes || '').trim())
      .map(shift => ({
        ...shift,
        note: String(shift.notes).trim(),
        employee_name: shift.snapshot_employee_name
          || employeeById[shift.employee_id]?.display_name
          || 'Dipendente',
        service_name: serviceById[shift.service_id]?.name || 'Servizio',
      }))
      .sort((left, right) => (
        `${left.shift_date}::${left.employee_name}`.localeCompare(`${right.shift_date}::${right.employee_name}`)
      ));
  }, [employees, gridShifts, services]);
  const toggleAvailability = () => {
    const nextOpen = !availabilityOpen;
    setAvailabilityOpen(nextOpen);
    window.setTimeout(() => {
      const isDesktop = window.matchMedia?.('(min-width: 768px)').matches;
      const target = nextOpen
        ? (isDesktop ? availabilityDesktopSummaryRef.current : availabilityMobileSummaryRef.current)
        : turniGridRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };
  const restDays = useMemo(() =>
    new Set(gridShifts.filter(s => s.shift_type === 'RIPOSO').map(s =>
      `${s.employee_id}::${s.shift_date}`)).size, [gridShifts]);

  const lastPublication = ws?.last_publication || null;
  const isReadOnlyVersion = Boolean(viewedVersion);
  const publishedVersion = Number(
    ws?.publication_status?.version || lastPublication?.version || 0,
  );
  const hasPublishedVersion = Boolean(lastPublication && publishedVersion);
  const draftPending = Boolean(ws?.publication_status?.republish_required);
  const draftVersion = hasPublishedVersion ? publishedVersion + 1 : null;
  const draftVisible = !isReadOnlyVersion && (editingWeek || draftPending || !hasPublishedVersion);
  const tableIsDraft = draftVisible;
  const tableVersion = isReadOnlyVersion ? viewedVersion : publishedVersion;
  const publicationDate = lastPublication?.published_at
    ? new Date(lastPublication.published_at).toLocaleString('it-IT', {
      dateStyle: 'short', timeStyle: 'short',
    })
    : null;
  const publicationAuthor = lastPublication?.published_by_name || null;
  const canShowPublish = Boolean(
    !isReadOnlyVersion && canPublish && period?.status === 'DRAFT'
      && !draftSaveFailed && !actionBusy
      && (!hasPublishedVersion || draftPending),
  );

  // ---- totals ----
  const totalProgrammate = useMemo(() =>
    Object.values(weeklyHours).reduce((a, b) => a + b, 0), [weeklyHours]);
  const totalByService = useMemo(() => {
    const m = {};
    gridShifts.filter(s => s.status === 'ACTIVE' && hasShiftTimeRange(s)).forEach(s => {
      m[s.service_id] = (m[s.service_id] || 0) + hoursOf(s);
    });
    return m;
  }, [gridShifts]);

  // ---- cell renderer ----
  const defaultsForService = (svc, day, emp) => {
    const date = day instanceof Date ? day : new Date(`${day}T12:00:00`);
    const weekday = (date.getDay() + 6) % 7;
    const assignment = emp?.assignments?.find(a => a.is_primary) || emp?.assignments?.[0];
    const configured = (ws?.default_schedules || []).find(item =>
      item.department_id === assignment?.department_id
      && item.weekday === weekday
      && item.service_id === svc?.id
      && item.active,
    );
    return configured
      ? {
        start: configured.start_time.slice(0, 5),
        end: configured.end_time.slice(0, 5),
        break: configured.break_minutes || 0,
      }
      : { start: '', end: '', break: 0 };
  };

  const openCreate = (emp, day, service, initialShiftType = 'LAVORO') => {
    if (!canCreate) return;
    const primary = emp.assignments.find(a => a.is_primary) || emp.assignments[0];
    const t = defaultsForService(service, day, emp);
    const pranzo = services.find(x => x.code === 'PRANZO');
    const cena = services.find(x => x.code === 'CENA');
    const pDef = defaultsForService(pranzo, day, emp);
    const cDef = defaultsForService(cena, day, emp);
    setShiftModal(normalizeShiftTimeFields({
      mode: 'create',
      employee_id: emp.employee_id, employee_name: emp.display_name,
      assignments: emp.assignments, assignment_id: primary.assignment_id,
      service_id: service.id, shift_date: iso(day),
      start_time: t.start, end_time: t.end,
      break_minutes: t.break, notes: '', shift_type: initialShiftType, double_shift: false,
       pranzo_start: pDef.start, pranzo_end: pDef.end, pranzo_break: pDef.break,
       cena_start: cDef.start, cena_end: cDef.end, cena_break: cDef.break,
    }));
  };
  const openEdit = (shift, emp) => {
    if (isReadOnlyVersion) return;
    const canOpenReplacementDetails = canReplace
      && period?.status === 'DRAFT'
      && shift?.status === 'ACTIVE';
    if (!canEdit && !canOpenReplacementDetails) return;
    const normalized = normalizeShiftTimeFields(shift);
    setShiftModal({
      mode: 'edit', id: shift.id,
      employee_id: shift.employee_id, employee_name: shift.snapshot_employee_name,
       status: shift.status,
      assignments: emp ? emp.assignments : [],
      assignment_id: shift.assignment_id, service_id: shift.service_id,
      shift_date: shift.shift_date,
       start_time: fmtHM(normalized.start_time), end_time: fmtHM(normalized.end_time),
        break_minutes: normalized.break_minutes, notes: shift.notes || '',
       shift_type: shift.shift_type || 'LAVORO',
    });
  };
  const handleShiftTypeChange = (nextType) => {
    setShiftModal(current => {
      if (!current) return current;
      const previousType = current.shift_type || 'LAVORO';
      if (!shiftHasTimeFields(nextType)) {
        return normalizeShiftTimeFields({
          ...current,
          shift_type: nextType,
          double_shift: false,
          pranzo_start: null, pranzo_end: null, pranzo_break: 0,
          cena_start: null, cena_end: null, cena_break: 0,
        });
      }
      if (!shiftHasTimeFields(previousType)) {
        const employee = { assignments: current.assignments || [] };
        const service = services.find(item => item.id === current.service_id);
        const pranzo = services.find(item => item.code === 'PRANZO');
        const cena = services.find(item => item.code === 'CENA');
        const currentDate = localDateFromValue(current.shift_date);
        const main = defaultsForService(service, currentDate, employee);
        const pDef = defaultsForService(pranzo, currentDate, employee);
        const cDef = defaultsForService(cena, currentDate, employee);
        return {
          ...current,
          shift_type: nextType,
          start_time: main.start,
          end_time: main.end,
          break_minutes: main.break,
          pranzo_start: pDef.start,
          pranzo_end: pDef.end,
          pranzo_break: pDef.break,
          cena_start: cDef.start,
          cena_end: cDef.end,
          cena_break: cDef.break,
        };
      }
      return { ...current, shift_type: nextType };
    });
  };

  const replacementContext = (shift, candidate) => [{
    id: shift.id,
    employeeName: candidate?.employee_name || 'Candidato selezionato',
    date: shift.shift_date,
    service: services.find(service => service.id === shift.service_id)?.name || 'Servizio',
    shift: `${fmtHM(shift.start_time)}–${fmtHM(shift.end_time)}`,
    availability: candidate?.availability?.state
      ? availabilityStateLabel(candidate.availability.state)
      : 'Verificata dal server',
  }];

  const openReplacementCandidates = async (shift) => {
    if (!canReplace || !period || period.status !== 'DRAFT' || shift?.status !== 'ACTIVE') return;
    setShiftModal(null);
    setReplacementModal({
      shift,
      loading: true,
      candidates: [],
      candidateCount: 0,
      selectedEmployeeId: '',
      error: null,
      readOnly: true,
    });
    try {
      const result = await apiFetch(`/api/turni/shifts/${shift.id}/replacement-candidates`);
      setReplacementModal(current => current?.shift?.id === shift.id ? {
        ...current,
        loading: false,
        shift: { ...shift, ...(result?.target_shift || {}) },
        candidates: result?.candidates || [],
        candidateCount: result?.candidate_count ?? (result?.candidates || []).length,
        readOnly: result?.read_only !== false,
        rankingVersion: result?.ranking_version,
      } : current);
    } catch (error) {
      setReplacementModal(current => current?.shift?.id === shift.id ? {
        ...current,
        loading: false,
        error: replacementErrorMessage(error, 'Errore caricamento candidati sostituzione.'),
      } : current);
    }
  };

  const confirmReplacement = async () => {
    const current = replacementModal;
    const candidateId = current?.selectedEmployeeId;
    if (!current || !candidateId || current.loading || actionBusy) return;
    const candidate = current.candidates.find(item => item.employee_id === candidateId);
    if (!candidate) {
      setReplacementModal(value => value ? {
        ...value,
        error: 'Seleziona un candidato presente nell’elenco.',
      } : value);
      return;
    }
    setActionBusy('replacement');
    setReplacementModal(value => value ? { ...value, error: null } : value);
    try {
      const result = await requestWithWarningRetry(
        (confirmed, reason) => apiFetch(
          `/api/turni/shifts/${current.shift.id}/replacement-confirmation`,
          {
            method: 'POST',
            body: JSON.stringify({
              replacement_employee_id: candidate.employee_id,
              confirm_warnings: confirmed,
              warning_reason: reason,
            }),
          },
        ),
        replacementContext(current.shift, candidate),
      );
      if (result === null) return;
      setReplacementModal(null);
      toast.success('Sostituzione confermata e turno ripubblicato');
      await reloadWorkspace();
    } catch (error) {
      setReplacementModal(value => value ? {
        ...value,
        error: replacementErrorMessage(error, 'Errore conferma sostituzione.'),
      } : value);
    } finally {
      setActionBusy('');
    }
  };

  const saveShift = async () => {
    const s = shiftModal;
    if (editingWeek) {
      const draftId = () => `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const makeShift = (id, serviceId, start, end, breakMinutes, notes) => normalizeShiftTimeFields({
        id, period_id: period.id, employee_id: s.employee_id, assignment_id: s.assignment_id,
        service_id: serviceId, shift_date: s.shift_date, start_time: start, end_time: end,
        break_minutes: Number(breakMinutes) || 0, notes: notes || null,
        shift_type: s.shift_type || 'LAVORO', status: 'ACTIVE',
        snapshot_employee_name: s.employee_name,
      });
       const isPersistedEdit = s.mode === 'edit' && !String(s.id).startsWith('draft-');
       const draftItems = isPersistedEdit
        ? [normalizeShiftTimeFields({
             id: s.id, employee_id: s.employee_id, assignment_id: s.assignment_id,
             service_id: s.service_id, shift_date: s.shift_date,
             start_time: s.start_time, end_time: s.end_time,
             break_minutes: Number(s.break_minutes) || 0, shift_type: s.shift_type || 'LAVORO',
          })]
         : s.double_shift
           ? (() => {
               const pranzo = services.find(x => x.code === 'PRANZO');
               const cena = services.find(x => x.code === 'CENA');
               if (!pranzo || !cena) throw new Error('Servizi Pranzo/Cena non disponibili');
               return [
                normalizeShiftTimeFields({
                   employee_id: s.employee_id, assignment_id: s.assignment_id,
                   service_id: pranzo.id, shift_date: s.shift_date,
                   start_time: s.pranzo_start, end_time: s.pranzo_end,
                   break_minutes: Number(s.pranzo_break) || 0, shift_type: s.shift_type || 'LAVORO',
                }),
                normalizeShiftTimeFields({
                   employee_id: s.employee_id, assignment_id: s.assignment_id,
                   service_id: cena.id, shift_date: s.shift_date,
                   start_time: s.cena_start, end_time: s.cena_end,
                   break_minutes: Number(s.cena_break) || 0, shift_type: s.shift_type || 'LAVORO',
                }),
               ];
             })()
          : [normalizeShiftTimeFields({
               employee_id: s.employee_id, assignment_id: s.assignment_id,
               service_id: s.service_id, shift_date: s.shift_date,
               start_time: s.start_time, end_time: s.end_time,
               break_minutes: Number(s.break_minutes) || 0, shift_type: s.shift_type || 'LAVORO',
            })];
       const applyDraftChange = (confirmed, reason, warnings) => {
         if (confirmed) rememberDraftWarningAcknowledgements(warnings, draftItems, reason);
         setWs(prev => {
           if (!prev) return prev;
           let additions = [];
           if (s.mode === 'create' && s.double_shift) {
             const pranzo = services.find(x => x.code === 'PRANZO');
             const cena = services.find(x => x.code === 'CENA');
             if (!pranzo || !cena) return prev;
             const groupId = draftId();
             additions = [
               { ...makeShift(`${groupId}-pranzo`, pranzo.id, s.pranzo_start, s.pranzo_end, s.pranzo_break, s.notes), double_shift_group_id: groupId },
               { ...makeShift(`${groupId}-cena`, cena.id, s.cena_start, s.cena_end, s.cena_break, null), double_shift_group_id: groupId },
             ];
           } else if (s.mode === 'create') {
             additions = [makeShift(draftId(), s.service_id, s.start_time, s.end_time, s.break_minutes, s.notes)];
           }
           const shifts = s.mode === 'edit'
                ? (prev.shifts || []).map(sh => sh.id === s.id ? normalizeShiftTimeFields({
                 ...sh, assignment_id: s.assignment_id, service_id: s.service_id,
                 shift_date: s.shift_date, start_time: s.start_time, end_time: s.end_time,
                 break_minutes: Number(s.break_minutes) || 0, notes: s.notes || null,
                 shift_type: s.shift_type || 'LAVORO',
                }) : sh)
             : [...(prev.shifts || []), ...additions];
           return { ...prev, shifts };
         });
         setShiftModal(null);
         toast.success(s.mode === 'create' ? 'Modifica aggiunta alla bozza' : 'Modifica applicata alla bozza');
         return true;
       };
       try {
         const warnings = await preflightWarnings(isPersistedEdit ? 'edit' : 'create', draftItems);
         const result = await runWithWarningConfirmation(
           warnings,
           (confirmed, reason) => applyDraftChange(confirmed, reason, warnings),
           warningContexts(draftItems),
         );
         if (result === null) return;
       } catch (e) {
         toast.error(e.message || 'Errore verifica disponibilità');
       }
      return;
    }
    try {
        if (s.mode === 'create') {
          if (s.double_shift) {
            // Atomic double shift via dedicated endpoint (transactional)
            const pranzo = services.find(x => x.code === 'PRANZO');
            const cena = services.find(x => x.code === 'CENA');
            if (!pranzo || !cena) throw new Error('Servizi Pranzo/Cena non disponibili');
             const doublePayload = {
                employee_id: s.employee_id, assignment_id: s.assignment_id,
                shift_date: s.shift_date,
                 pranzo: {
                  service_id: pranzo.id,
                  start_time: s.pranzo_start, end_time: s.pranzo_end,
                  break_minutes: Number(s.pranzo_break) || 0, notes: s.notes || null,
                },
                cena: {
                  service_id: cena.id,
                  start_time: s.cena_start, end_time: s.cena_end,
                  break_minutes: Number(s.cena_break) || 0, notes: null,
                },
             };
             const doubleItems = [
               {
                 employee_id: s.employee_id, assignment_id: s.assignment_id,
                 service_id: pranzo.id, shift_date: s.shift_date,
                 start_time: doublePayload.pranzo.start_time, end_time: doublePayload.pranzo.end_time,
                 shift_type: 'LAVORO',
               },
               {
                 employee_id: s.employee_id, assignment_id: s.assignment_id,
                 service_id: cena.id, shift_date: s.shift_date,
                 start_time: doublePayload.cena.start_time, end_time: doublePayload.cena.end_time,
                 shift_type: 'LAVORO',
               },
             ];
             const warnings = await preflightWarnings('create', doubleItems);
              const result = await runWithWarningConfirmation(warnings, (confirmed, reason) =>
               apiFetch(`/api/turni/periods/${period.id}/double-shift`, {
                 method: 'POST', body: JSON.stringify({
                   ...doublePayload, confirm_warnings: confirmed, warning_reason: reason,
                 }),
                }), warningContexts(doubleItems));
             if (result === null) return;
            toast.success('Doppio turno creato');
          } else {
              const shiftPayload = normalizeShiftTimeFields({
                employee_id: s.employee_id, assignment_id: s.assignment_id,
                service_id: s.service_id, shift_date: s.shift_date,
                start_time: s.start_time, end_time: s.end_time,
                 break_minutes: Number(s.break_minutes) || 0, notes: s.notes || null,
                 shift_type: s.shift_type || 'LAVORO',
              });
             const warnings = await preflightWarnings('create', [shiftPayload]);
              const result = await runWithWarningConfirmation(warnings, (confirmed, reason) =>
               apiFetch(`/api/turni/periods/${period.id}/shifts`, {
                 method: 'POST', body: JSON.stringify({
                   ...shiftPayload, confirm_warnings: confirmed, warning_reason: reason,
                 }),
                }), warningContexts([shiftPayload]));
             if (result === null) return;
            toast.success('Turno creato');
          }
      } else {
          const shiftPayload = normalizeShiftTimeFields({
            assignment_id: s.assignment_id, service_id: s.service_id,
            shift_date: s.shift_date, start_time: s.start_time, end_time: s.end_time,
            break_minutes: Number(s.break_minutes) || 0, notes: s.notes || null,
             shift_type: s.shift_type || 'LAVORO',
          });
         const warnings = await preflightWarnings('edit', [{ ...shiftPayload, id: s.id, employee_id: s.employee_id }]);
          const result = await runWithWarningConfirmation(warnings, (confirmed, reason) =>
           apiFetch(`/api/turni/shifts/${s.id}`, {
             method: 'PATCH', body: JSON.stringify({
               ...shiftPayload, confirm_warnings: confirmed, warning_reason: reason,
             }),
            }), warningContexts([{ ...shiftPayload, id: s.id, employee_id: s.employee_id }]));
         if (result === null) return;
        toast.success('Turno aggiornato');
      }
      setShiftModal(null); await reloadWorkspace();
    } catch (e) { toast.error(e.message || 'Errore salvataggio'); }
  };
  const cancelShift = async () => {
    if (!shiftModal?.id) return;
    if (!window.confirm('Annullare questo turno?')) return;
    if (editingWeek) {
      setWs(prev => ({
        ...prev,
        shifts: (prev?.shifts || []).map(sh => sh.id === shiftModal.id
          ? { ...sh, status: 'CANCELLED' } : sh),
      }));
      setShiftModal(null);
      toast.success('Turno annullato nella bozza');
      return;
    }
    try {
      await apiFetch(`/api/turni/shifts/${shiftModal.id}/cancel`, { method: 'POST' });
      toast.success('Turno annullato');
      setShiftModal(null); await reloadWorkspace();
    } catch (e) { toast.error(e.message || 'Errore'); }
  };

  if (!canView) {
    return <div className="p-8 text-center text-stone-500" data-testid="turni-no-access">
      Non hai i permessi per visualizzare Turni.
    </div>;
  }

  const archivePeriod = async () => {
    if (!period || isReadOnlyVersion) return;
    if (!window.confirm('Archiviare il periodo? I turni resteranno nello storico.')) return;
    if (!window.confirm('Confermi definitivamente l’archiviazione di questo periodo?')) return;
    setActionBusy('archive');
    try {
      await apiFetch(`/api/turni/periods/${period.id}/archive`, { method: 'POST' });
      toast.success('Periodo archiviato');
      setPeriod(null); setWs(null);
      await loadPeriodAndWorkspace();
    } catch (e) { toast.error(e.message || 'Errore archiviazione'); }
    finally { setActionBusy(''); }
  };
  const reopenPeriod = async () => {
    if (!period || period.status !== 'ARCHIVED') return;
    setActionBusy('reopen');
    try {
      await apiFetch(`/api/turni/periods/${period.id}/reopen`, { method: 'POST' });
      toast.success('Bozza riaperta');
      await loadPeriodAndWorkspace();
    } catch (e) { toast.error(e.message || 'Errore riapertura bozza'); }
    finally { setActionBusy(''); }
  };
  const copyPreviousWeek = async () => {
    if (!period || period.status !== 'DRAFT' || isReadOnlyVersion) return;
    if (!window.confirm('Copiare i turni della settimana precedente? I turni già presenti non verranno duplicati.')) return;
    setActionBusy('copy');
    try {
      const r = await requestWithWarningRetry((confirmed, reason) =>
        apiFetch(`/api/turni/periods/${period.id}/copy-previous`, {
          method: 'POST',
          body: JSON.stringify({ confirm_warnings: confirmed, warning_reason: reason }),
        }), warningContexts(ws?.shifts || []));
      if (r === null) return;
      toast.success(`Settimana copiata: ${r.created} creati, ${r.skipped} già presenti`);
      await reloadWorkspace();
    } catch (e) { toast.error(e.message || 'Errore copia settimana'); }
    finally { setActionBusy(''); }
  };
  const isIosLike = () => (
    typeof navigator !== 'undefined'
    && (/iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))
  );

  const periodPdfPath = () => {
    const params = new URLSearchParams();
    params.set('view_start', iso(weekStart));
    params.set('view_end', iso(weekEnd));
    if (showCancelled) params.set('include_cancelled', 'true');
    if (viewedVersion) params.set('version', String(viewedVersion));
    const query = params.toString();
    return `/api/turni/periods/${period.id}/pdf${query ? `?${query}` : ''}`;
  };

  const periodPdfFilename = () => (
    `turni-${iso(weekStart)}${viewedVersion ? `-v${viewedVersion}` : ''}.pdf`
  );

  const getPeriodPdfBlob = () => {
    const key = periodPdfPath();
    const cached = pdfBlobCacheRef.current.get(key);
    if (cached) return cached;
    const request = apiFetchBlob(key).then((blob) => {
      const type = (blob?.type || '').toLowerCase();
      if (!blob || !blob.size || type !== 'application/pdf') {
        throw new Error('Il server non ha restituito un PDF valido');
      }
      return blob;
    }).catch((error) => {
      pdfBlobCacheRef.current.delete(key);
      throw error;
    });
    pdfBlobCacheRef.current.set(key, request);
    return request;
  };

  const primePeriodPdf = () => {
    if (period) getPeriodPdfBlob().catch(() => {});
  };

  const openPdfForIos = (blob, filename, reservedWindow = null) => {
    const url = URL.createObjectURL(blob);
    if (reservedWindow && !reservedWindow.closed) {
      reservedWindow.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const downloadPeriodPdf = async () => {
    if (!period) return;
    setActionBusy('download');
    let reservedWindow = null;
    if (isIosLike()) {
      // Reserve the tab while the click activation is still live. Safari can
      // block a new tab opened only after the authenticated fetch completes.
      reservedWindow = window.open('', '_blank');
      if (reservedWindow) reservedWindow.opener = null;
    }
    try {
      const blob = await getPeriodPdfBlob();
      const filename = periodPdfFilename();
      if (isIosLike()) {
        openPdfForIos(blob, filename, reservedWindow);
        toast.success('PDF aperto: puoi salvarlo o condividerlo da Safari');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        toast.success('PDF scaricato');
      }
    } catch (e) {
      if (reservedWindow && !reservedWindow.closed) reservedWindow.close();
      toast.error(e.message || 'Errore download PDF');
    }
    finally { setActionBusy(''); }
  };

  const sharePeriodPdf = async () => {
    if (!period) return;
    setActionBusy('share');
    try {
      const blob = await getPeriodPdfBlob();
      const file = new File([blob], periodPdfFilename(), { type: 'application/pdf' });
      const canShareFiles = typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        await navigator.share({ title: 'Turni', text: 'Piano turni', files: [file] });
        toast.success('PDF condiviso');
      } else {
        openPdfForIos(blob, file.name);
        toast.success(isIosLike()
          ? 'Condivisione file non disponibile: PDF aperto in una nuova scheda'
          : 'Condivisione file non disponibile: PDF aperto per il salvataggio');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        try {
          const blob = await getPeriodPdfBlob();
          openPdfForIos(blob, periodPdfFilename());
          toast.success('Condivisione nativa non disponibile: PDF aperto per il salvataggio');
        } catch (fallbackError) {
          toast.error(fallbackError.message || e.message || 'Errore condivisione PDF');
        }
      }
    } finally { setActionBusy(''); }
  };
  const openVersions = async () => {
    if (!period) return;
    setActionBusy('versions');
    try {
      await loadVersionHistory(period.id);
      setVersionsOpen(true);
    } catch (e) { toast.error(e.message || 'Errore caricamento versioni'); }
    finally { setActionBusy(''); }
  };
  const undoLastOperation = async () => {
    if (!period || period.status !== 'DRAFT' || isReadOnlyVersion) return;
    if (!window.confirm('Annullare l’ultima operazione del periodo?')) return;
    setActionBusy('undo');
    try {
      const r = await apiFetch(`/api/turni/periods/${period.id}/undo-last`, { method: 'POST' });
      toast.success(r.action === 'period.copied_previous' ? 'Copia annullata' : 'Ultima operazione annullata');
      await loadPeriodAndWorkspace();
    } catch (e) { toast.error(e.message || 'Errore annullamento operazione'); }
    finally { setActionBusy(''); }
  };
  const openPeriodEditor = () => {
    if (!period || editingWeek || isReadOnlyVersion) return;
    setPeriodModal({
      start_date: period.start_date,
      end_date: period.end_date,
    });
  };
  const savePeriodEdit = async () => {
    if (!period || !periodModal || isReadOnlyVersion) return;
    if (periodModal.end_date < periodModal.start_date) {
      toast.error('La data fine deve seguire la data inizio');
      return;
    }
    setActionBusy('edit');
    try {
      await apiFetch(`/api/turni/periods/${period.id}`, {
        method: 'PATCH',
        body: JSON.stringify(periodModal),
      });
      toast.success('Periodo aggiornato');
      setPeriodModal(null);
      await loadPeriodAndWorkspace();
    } catch (e) { toast.error(e.message || 'Errore aggiornamento periodo'); }
    finally { setActionBusy(''); }
  };
  const saveDraft = async () => {
    if (!period || !editingWeek || !weekSnapshot) return;
    setActionBusy('draft');
    setDraftSaveFailed(false);
    try {
        const current = (ws?.shifts || []).filter(shift =>
        shift.shift_date >= iso(weekStart) && shift.shift_date <= iso(weekEnd));
      // The batch is the complete visible state, rather than a list of
      // mutations.  In particular, retain cancelled persisted shifts so the
      // server can apply their cancellation in the same transaction.
      const preflightItems = current
        .filter(shift => shift.status !== 'CANCELLED')
          .map(shift => normalizeShiftTimeFields({
          ...(shift.id.startsWith('draft-') ? {} : { id: shift.id }),
          employee_id: shift.employee_id, assignment_id: shift.assignment_id,
          service_id: shift.service_id, shift_date: shift.shift_date,
          start_time: shift.start_time, end_time: shift.end_time,
          break_minutes: Number(shift.break_minutes) || 0,
          shift_type: shift.shift_type || 'LAVORO',
          }));
      const warnings = await preflightWarnings('edit', preflightItems);
      const acknowledgedWarnings = warnings.filter(warning =>
        isDraftWarningAcknowledged(warning, preflightItems));
      const pendingWarnings = warnings.filter(warning =>
        !isDraftWarningAcknowledged(warning, preflightItems));
      const acknowledgedReason = acknowledgedWarnings
        .map(warning => draftWarningReason(warning, preflightItems))
        .find(reason => reason);
      const persistDraft = async (confirmWarnings, warningReason) => {
        return apiFetch(`/api/turni/periods/${period.id}/draft-batch`, {
          method: 'POST',
          body: JSON.stringify(buildWeeklyDraftBatch(
            current.map(normalizeShiftTimeFields), ws?.draft_state_token, iso(weekStart), iso(weekEnd),
            confirmWarnings, warningReason,
          )),
        });
      };
      const result = pendingWarnings.length
        ? await runWithWarningConfirmation(pendingWarnings, persistDraft, warningContexts(preflightItems))
        : warnings.length
          ? await persistDraft(true, acknowledgedReason || null)
          : await persistDraft(false, null);
      if (result === null) return;
      toast.success('Bozza salvata');
      setDraftSaveFailed(false);
      setEditingWeek(false);
      setWeekSnapshot(null);
      draftWarningAcknowledgementsRef.current.clear();
      await loadPeriodAndWorkspace();
    } catch (e) {
      setDraftSaveFailed(true);
      const detail = e?.body?.detail;
      const code = e?.body?.code || (typeof detail === 'object' ? detail?.code : detail)
        || e?.code;
      if (code === 'PERIOD_STALE' || e?.status === 409) {
        toast.error('Il periodo è cambiato da un altro utente (PERIOD_STALE). Ricarica la settimana, rivedi le modifiche e riprova.');
      } else {
        toast.error(e.message || 'Errore salvataggio bozza');
      }
    }
    finally { setActionBusy(''); }
  };
  const startWeekEditing = async () => {
    if (!ws || !period || actionBusy || isReadOnlyVersion) return;
    draftWarningAcknowledgementsRef.current.clear();
    setDraftSaveFailed(false);
    setActionBusy('seed');
    try {
      let editingWorkspace = ws;
      const currentShifts = ws.shifts || [];
      if (!draftPending && currentShifts.length === 0) {
        await apiFetch(`/api/turni/periods/${period.id}/draft-from-publication`, {
          method: 'POST',
        });
        const refreshed = await loadPeriodAndWorkspace();
        editingWorkspace = refreshed?.workspace || editingWorkspace;
      } else if (!draftPending && Array.isArray(ws.published_shifts)) {
        editingWorkspace = { ...ws, shifts: ws.published_shifts };
        setWs(editingWorkspace);
      }
      setWeekSnapshot(JSON.parse(JSON.stringify(editingWorkspace)));
      setEditingWeek(true);
    } catch (error) {
      toast.error(error.message || 'Non è stato possibile preparare la bozza.');
    } finally {
      setActionBusy('');
    }
  };
  const cancelWeekEditing = () => {
    if (actionBusy) return;
    draftWarningAcknowledgementsRef.current.clear();
    if (weekSnapshot) setWs(weekSnapshot);
    setShiftModal(null);
    setEditingWeek(false);
    setWeekSnapshot(null);
    setDraftSaveFailed(false);
    toast.success('Modifiche annullate');
  };
  const requestPublication = () => {
    if (!period || editingWeek || draftSaveFailed || !canShowPublish) return;
    setPublicationConfirm({
      version: hasPublishedVersion ? draftVersion : 1,
    });
  };
  const publishPeriod = async () => {
    if (!period || editingWeek || draftSaveFailed || !canShowPublish) return;
    setPublicationConfirm(null);
    setActionBusy('publish');
    try {
      const r = await requestWithWarningRetry((confirmed, reason) =>
        apiFetch(`/api/turni/periods/${period.id}/publish`, {
          method: 'POST',
          body: JSON.stringify(buildPublicationPayload(
            period, iso(weekStart), iso(weekEnd), ws?.draft_state_token, confirmed, reason,
          )),
        }), warningContexts(gridShifts.filter(shift => shift.status === 'ACTIVE')));
      if (r === null) return;
       toast.success(`Pubblicata la versione ${r.version}`);
       await reloadWorkspace();
       await loadVersionHistory(period.id);
    } catch (e) {
      const detail = e?.body?.detail;
      const code = e?.body?.code || (typeof detail === 'object' ? detail?.code : detail)
        || e?.code;
      if (code === 'PERIOD_STALE' || e?.status === 409) {
        toast.error('La bozza è stata modificata da un altro utente. Ricarica i turni prima di pubblicare.');
        await reloadWorkspace();
      } else {
        toast.error(e.message || 'Errore pubblicazione');
      }
    }
    finally { setActionBusy(''); }
  };

  const openExistingPeriod = () => {
    const existingStart = periodConflict?.start_date;
    const existingEnd = periodConflict?.end_date;
    if (!existingStart || !existingEnd) return;
    const currentStart = iso(weekStart);
    const currentEnd = iso(weekEnd);
    // Keep the current view when it is already contained. Otherwise use the
    // existing period's own start (not its ISO-week start), which guarantees
    // the seven-day view is contained whenever the period can contain one.
    const contained = dateRangeContains(existingStart, existingEnd, currentStart, currentEnd);
    if (!contained) {
      const periodLength = Math.round(
        (localDateFromValue(existingEnd) - localDateFromValue(existingStart)) / 86400000,
      ) + 1;
      if (periodLength < 7) return;
      setWeekStart(localDateFromValue(existingStart));
    }
    setPeriodConflict(null);
    if (contained) loadPeriodAndWorkspace();
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-0px)] pb-[76px] md:pb-0 md:space-y-3 bg-app-background" data-testid="turni-page">
       {/* --- MOBILE RESTAURANTOS HEADER (hidden ≥md) --- */}
      <header className="ros-mobile-topbar md:hidden sticky top-0 z-30"
        data-testid="turni-mobile-topbar">
         <div className="flex items-center gap-3 px-3 py-2 min-h-[60px]">
          <button onClick={openMobileMenu} data-testid="turni-mobile-menu-btn"
             className="w-10 h-10 -ml-1 flex items-center justify-center rounded-xl text-stone-600 hover:bg-stone-100 border border-stone-200">
            <MenuIcon className="w-5 h-5" />
          </button>
           <img src="/brand/restaurantos-logo-symbol.svg" alt="" className="w-8 h-8" />
           <div className="flex-1 min-w-0 leading-none">
             <div className="text-[11px] font-semibold text-stone-500 tracking-tight mb-1">RestaurantOS</div>
             <div className="font-display font-bold text-brand-navy text-xl tracking-tight">Turni</div>
          </div>
          <NotificationsBell />
        </div>
      </header>

      {/* Content padding wrapper — mobile compact, desktop already has px-8 py-8 from Layout */}
       <div className="flex-1 min-w-0 flex flex-col gap-2 px-3 pt-2 md:px-0 md:pt-0 md:gap-3">

      {/* Compact scope pill (single line) */}
      <button onClick={() => setScopeOpen(true)} data-testid="turni-scope-open"
        disabled={editingWeek || !!actionBusy}
        className="w-full flex items-center justify-between gap-2 bg-white rounded-xl border border-stone-200 px-3 h-11 md:h-12 md:rounded-2xl md:px-4 hover:border-stone-300 transition">
        <div className="text-left min-w-0 truncate text-[13px] md:text-sm">
          {company ? (
            <>
              <span className="font-semibold text-stone-900">{company.name}</span>
              {location && <> <span className="text-stone-400">·</span> <span className="text-stone-700">{location.name}</span></>}
              {(department || locationId) && <> <span className="text-stone-400">·</span> <span className="text-brand-green font-medium">{department?.name || 'Tutti i reparti'}</span></>}
            </>
          ) : <span className="text-stone-500">Seleziona azienda · locale · reparto</span>}
        </div>
        <ChevronDown className="w-4 h-4 text-stone-400 shrink-0" />
      </button>

      {/* Week header + toolbar (compact, mobile-first) */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-stone-200 px-2 py-2 md:p-3">
           <div className="flex items-center gap-1">
          <button data-testid="turni-week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}
            disabled={editingWeek || !!actionBusy}
            className="w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-xl border border-stone-200 flex items-center justify-center hover:bg-stone-50">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 text-center leading-tight">
            <div className="font-display font-semibold text-[15px] md:text-lg text-brand-navy" data-testid="turni-week-label">
              {weekRange(weekStart, weekEnd)}
            </div>
            <div className="text-[10px] md:text-[11px] text-stone-500">
              {employees.length ? `${employees.length} persone · ${totalProgrammate.toFixed(0)} h` : '—'}
            </div>
          </div>
           <button data-testid="turni-period-calendar" onClick={openPeriodEditor}
             disabled={isReadOnlyVersion || !period || editingWeek || !!actionBusy}
             title="Modifica periodo"
             className="w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-xl border border-stone-200 flex items-center justify-center hover:bg-stone-50 disabled:opacity-40">
             <CalendarIcon className="w-4 h-4 text-stone-500" />
           </button>
           <button data-testid="turni-week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}
             disabled={editingWeek || !!actionBusy}
            className="w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-xl border border-stone-200 flex items-center justify-center hover:bg-stone-50">
            <ChevronRight className="w-4 h-4" />
          </button>
           <button data-testid="turni-search-toggle" onClick={() => setSearchOpen(v => !v)}
            className="w-8 h-8 md:w-9 md:h-9 rounded-lg md:rounded-xl border border-stone-200 flex items-center justify-center hover:bg-stone-50 text-brand-green">
            <Search className="w-4 h-4" />
          </button>
        </div>
         {searchOpen && (
           <div className="mt-2 relative">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
             <Input autoFocus data-testid="turni-search-input" value={employeeSearch}
               onChange={(e) => setEmployeeSearch(e.target.value)}
               placeholder="Cerca dipendente, mansione o reparto"
               className="pl-9 h-10 bg-stone-50" />
           </div>
         )}

         {/* --- Toolbar --- */}
          {/* Mobile toolbar: actions stay visible in a two-column layout. */}
          <div className="mt-2 md:hidden grid grid-cols-2 gap-1.5 pb-0.5">
           {canEdit && (
                <ToolbarBtn testid="turni-tb-edit-m" tone={editingWeek ? 'soft' : 'neutral'} icon={Pencil}
                  label={editingWeek ? 'Editing attivo' : draftPending ? 'Modifica bozza' : 'Modifica'}
                  disabled={isReadOnlyVersion || !period || period.status !== 'DRAFT' || !!actionBusy || editingWeek}
                 onClick={startWeekEditing} compact />
           )}
           {canEdit && editingWeek && (
             <ToolbarBtn testid="turni-tb-cancel-m" tone="neutral" icon={Pencil} label="Annulla"
               disabled={!!actionBusy} onClick={cancelWeekEditing} compact />
           )}
            {canEdit && (editingWeek || draftPending) && (
             <ToolbarBtn testid="turni-tb-draft-m" tone="soft" icon={Save} label={actionBusy === 'draft' ? 'Salvataggio…' : 'Salva bozza'}
                disabled={!period || period.status !== 'DRAFT' || !editingWeek || !!actionBusy} onClick={saveDraft} compact />
          )}
           {canPublish && (!hasPublishedVersion || draftPending || editingWeek) ? (
              <ToolbarBtn testid="turni-tb-publish-m" tone="dark" icon={CalendarIcon}
                 label={actionBusy === 'publish' ? 'Pubblicazione…' : hasPublishedVersion ? `Pubblica v${draftVersion}` : 'Pubblica'}
                 disabled={!period || period.status !== 'DRAFT' || editingWeek || draftSaveFailed || !!actionBusy || !canShowPublish}
                 onClick={requestPublication} compact />
           ) : null}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="turni-tb-more-m"
                 className="inline-flex h-11 min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-stone-200 px-2 text-xs font-semibold text-stone-700 hover:bg-stone-50">
                <MoreHorizontal className="w-4 h-4 text-stone-500" />
                 <span>Menu</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {!period && canCreate && (
                <DropdownMenuItem data-testid="turni-action-create" onClick={createPeriod} disabled={!!actionBusy || !companyId || !locationId}>
                  {actionBusy === 'create' ? <Loader2 className="animate-spin" /> : <Plus />}
                  Crea periodo
                </DropdownMenuItem>
              )}
              {period?.status === 'ARCHIVED' && canEdit && (
                <DropdownMenuItem data-testid="turni-action-reopen" onClick={reopenPeriod} disabled={!!actionBusy}>
                  {actionBusy === 'reopen' ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                  Riapri bozza
                </DropdownMenuItem>
              )}
               {period?.status === 'DRAFT' && canCreate && !isReadOnlyVersion && (
                <DropdownMenuItem data-testid="turni-action-copy-previous" onClick={copyPreviousWeek} disabled={!!actionBusy}>
                  {actionBusy === 'copy' ? <Loader2 className="animate-spin" /> : <Copy />}
                  Copia settimana precedente
                </DropdownMenuItem>
              )}
              {period && (
                <>
                  <DropdownMenuItem data-testid="turni-action-toggle-cancelled"
                    onClick={() => setShowCancelled(value => !value)}>
                    {showCancelled ? 'Nascondi annullati' : 'Mostra annullati'}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="turni-action-download-pdf" onClick={downloadPeriodPdf} disabled={!!actionBusy}>
                    {actionBusy === 'download' ? <Loader2 className="animate-spin" /> : <Download />}
                    Scarica PDF
                  </DropdownMenuItem>
                   <DropdownMenuItem data-testid="turni-action-share-pdf" onPointerDown={primePeriodPdf} onClick={sharePeriodPdf} disabled={!!actionBusy}>
                    {actionBusy === 'share' ? <Loader2 className="animate-spin" /> : <Share2 />}
                    Condividi PDF
                  </DropdownMenuItem>
                   <DropdownMenuItem data-testid="turni-action-versions" onClick={openVersions} disabled={!!actionBusy}>
                    {actionBusy === 'versions' ? <Loader2 className="animate-spin" /> : <History />}
                     Storico versioni
                  </DropdownMenuItem>
                </>
              )}
               {period?.status === 'DRAFT' && canEdit && !isReadOnlyVersion && (
                <DropdownMenuItem data-testid="turni-action-undo" onClick={undoLastOperation} disabled={!!actionBusy}>
                  {actionBusy === 'undo' ? <Loader2 className="animate-spin" /> : <Undo2 />}
                  Annulla ultima operazione
                </DropdownMenuItem>
              )}
               {canEdit && !isReadOnlyVersion && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="turni-action-archive" onClick={archivePeriod} disabled={!period || !!actionBusy}
                    className="text-red-600 focus:text-red-700">
                    {actionBusy === 'archive' ? <Loader2 className="animate-spin" /> : <Trash2 />}
                    Archivia periodo
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* DESKTOP toolbar (≥md): full inline row (existing behavior) */}
        <div className="mt-3 hidden md:flex items-center gap-2 overflow-x-auto no-scrollbar">
          {canEdit && (
             <ToolbarBtn testid="turni-tb-edit" tone={editingWeek ? 'soft' : 'neutral'} icon={Pencil}
               label={editingWeek ? 'Editing attivo' : draftPending ? 'Modifica bozza' : 'Modifica'}
                disabled={isReadOnlyVersion || !period || period.status !== 'DRAFT' || !!actionBusy || editingWeek}
               onClick={startWeekEditing} />
          )}
           {canEdit && (
             <ToolbarBtn testid="turni-tb-delete" tone="danger" icon={Trash2} label="Elimina"
               disabled={isReadOnlyVersion || !period || !!actionBusy}
              onClick={archivePeriod} />
          )}
          {canEdit && (
             <>
              {editingWeek && (
                <ToolbarBtn testid="turni-tb-cancel" tone="neutral" icon={Pencil} label="Annulla"
                  disabled={!!actionBusy} onClick={cancelWeekEditing} />
              )}
               {(editingWeek || draftPending) && <ToolbarBtn testid="turni-tb-draft" tone="soft" icon={Save} label="Salva bozza"
               disabled={!period || period.status !== 'DRAFT' || !editingWeek || !!actionBusy} onClick={saveDraft} />
               }
             </>
          )}
           {!isReadOnlyVersion && canPublish && (!hasPublishedVersion || draftPending || editingWeek) && (
                <ToolbarBtn testid="turni-tb-publish" tone="dark" icon={CalendarIcon}
                label={hasPublishedVersion ? `Pubblica v${draftVersion}` : 'Pubblica'}
                disabled={!period || period.status !== 'DRAFT' || editingWeek || draftSaveFailed || !!actionBusy || !canShowPublish}
                onClick={requestPublication} />
          )}
           <DropdownMenu>
             <DropdownMenuTrigger asChild>
               <button
                 data-testid="turni-tb-more"
                 className="inline-flex min-h-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-stone-200 px-3 text-[13px] font-semibold text-stone-700 hover:bg-stone-50"
               >
                 <MoreHorizontal className="h-4 w-4 text-stone-500" />
                 Menu
               </button>
             </DropdownMenuTrigger>
             <DropdownMenuContent align="end" className="w-64">
               {!period && canCreate && (
                 <DropdownMenuItem data-testid="turni-action-create-desktop" onClick={createPeriod} disabled={!!actionBusy || !companyId || !locationId}>
                   {actionBusy === 'create' ? <Loader2 className="animate-spin" /> : <Plus />}
                   Crea periodo
                 </DropdownMenuItem>
               )}
               {period?.status === 'ARCHIVED' && canEdit && (
                 <DropdownMenuItem data-testid="turni-action-reopen-desktop" onClick={reopenPeriod} disabled={!!actionBusy}>
                   {actionBusy === 'reopen' ? <Loader2 className="animate-spin" /> : <RotateCcw />}
                   Riapri bozza
                 </DropdownMenuItem>
               )}
               {period?.status === 'DRAFT' && canCreate && !isReadOnlyVersion && (
                 <DropdownMenuItem data-testid="turni-action-copy-previous-desktop" onClick={copyPreviousWeek} disabled={!!actionBusy}>
                   {actionBusy === 'copy' ? <Loader2 className="animate-spin" /> : <Copy />}
                   Copia settimana precedente
                 </DropdownMenuItem>
               )}
               {period && (
                 <>
                   <DropdownMenuItem data-testid="turni-action-toggle-cancelled-desktop"
                     onClick={() => setShowCancelled(value => !value)}>
                     {showCancelled ? 'Nascondi annullati' : 'Mostra annullati'}
                   </DropdownMenuItem>
                   <DropdownMenuItem data-testid="turni-action-download-pdf-desktop" onClick={downloadPeriodPdf} disabled={!!actionBusy}>
                     {actionBusy === 'download' ? <Loader2 className="animate-spin" /> : <Download />}
                     Scarica PDF
                   </DropdownMenuItem>
                    <DropdownMenuItem data-testid="turni-action-share-pdf-desktop" onPointerDown={primePeriodPdf} onClick={sharePeriodPdf} disabled={!!actionBusy}>
                     {actionBusy === 'share' ? <Loader2 className="animate-spin" /> : <Share2 />}
                     Condividi PDF
                   </DropdownMenuItem>
                   <DropdownMenuItem data-testid="turni-action-versions-desktop" onClick={openVersions} disabled={!!actionBusy}>
                     {actionBusy === 'versions' ? <Loader2 className="animate-spin" /> : <History />}
                     Storico versioni
                   </DropdownMenuItem>
                 </>
               )}
               {period?.status === 'DRAFT' && canEdit && !isReadOnlyVersion && (
                 <DropdownMenuItem data-testid="turni-action-undo-desktop" onClick={undoLastOperation} disabled={!!actionBusy}>
                   {actionBusy === 'undo' ? <Loader2 className="animate-spin" /> : <Undo2 />}
                   Annulla ultima operazione
                 </DropdownMenuItem>
               )}
               {canEdit && !isReadOnlyVersion && (
                 <>
                   <DropdownMenuSeparator />
                   <DropdownMenuItem data-testid="turni-action-archive-desktop" onClick={archivePeriod} disabled={!period || !!actionBusy}
                     className="text-red-600 focus:text-red-700">
                     {actionBusy === 'archive' ? <Loader2 className="animate-spin" /> : <Trash2 />}
                     Archivia periodo
                   </DropdownMenuItem>
                 </>
               )}
             </DropdownMenuContent>
           </DropdownMenu>
        </div>
      </div>

        {editingWeek && (
         <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
           data-testid="turni-editing-state">
            <strong>Bozza in modifica</strong>: la versione pubblicata resta invariata.
            Tocca una cella per modificare il turno, poi salva la bozza.
         </div>
       )}
       {draftSaveFailed && (
         <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
           role="alert" data-testid="turni-draft-save-error">
           La bozza non è stata salvata integralmente. Correggi o ricarica la settimana e riprova; la pubblicazione è bloccata.
         </div>
       )}
       {isReadOnlyVersion && (
         <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-950"
           data-testid="turni-viewed-version">
           <span>
             Visualizzata <strong>versione {viewedVersion}</strong> · pubblicata · sola lettura
           </span>
           <Button type="button" variant="outline" size="sm" onClick={() => setViewedVersion(null)}>
             Torna alla bozza corrente
           </Button>
         </div>
       )}
        {period && !isReadOnlyVersion && (
          <div
            className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs"
            data-testid="turni-publication-summary"
          >
            <div className="flex flex-wrap items-center gap-2">
              {hasPublishedVersion ? (
                <>
                  <span
                    className="rounded-full bg-emerald-700 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white"
                    data-testid="turni-publication-status"
                  >
                    VERSIONE PUBBLICATA · V{publishedVersion}
                  </span>
                  <span className="text-stone-500">
                    {publicationDate ? `Pubblicata il ${publicationDate}` : 'Data non disponibile'}
                    {publicationAuthor ? ` · ${publicationAuthor}` : ''}
                  </span>
                </>
              ) : (
                <>
                  <span
                    className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white"
                    data-testid="turni-draft-status"
                  >
                    BOZZA — MODIFICABILE
                  </span>
                  <span className="text-stone-500">Nessuna versione pubblicata</span>
                </>
              )}
            </div>
            {(draftPending || editingWeek) && hasPublishedVersion && (
              <span className="text-amber-800" data-testid="turni-draft-status">
                {editingWeek
                  ? `Bozza in modifica · versione prevista ${draftVersion}`
                  : `Bozza salvata · versione prevista ${draftVersion}`}
              </span>
            )}
          </div>
        )}

       {/* Empty states */}
      {!locationId && (
        <div className="rounded-xl md:rounded-2xl border border-dashed border-stone-300 bg-white p-8 md:p-10 text-center text-stone-500 text-sm" data-testid="turni-select-scope-empty">
          Apri il selettore in alto per scegliere azienda, locale e reparto.
        </div>
      )}
      {locationId && loading && (
        <div className="flex justify-center py-10 md:py-14 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      )}
      {locationId && !loading && !period && (
        <div className="rounded-xl md:rounded-2xl border border-dashed border-stone-300 bg-white p-8 md:p-10 text-center" data-testid="turni-empty-period">
          <CalendarIcon className="w-9 h-9 md:w-10 md:h-10 text-stone-300 mx-auto mb-3" />
          {periodConflict ? (
            <>
              <p className="text-stone-800 text-sm font-semibold mb-2">Esiste già un periodo sovrapposto.</p>
              <p className="text-stone-600 text-sm mb-4">
                {periodConflict.start_date && periodConflict.end_date
                  ? `Periodo esistente: ${periodLabel(periodConflict.start_date, periodConflict.end_date)}. `
                  : (periodConflict.message || 'Non è possibile identificare il periodo esistente. ')}
                {periodConflict.start_date && periodConflict.end_date
                  ? 'Apri il periodo esistente oppure annulla: non verrà creato un nuovo periodo.'
                  : 'Annulla per chiudere questo avviso.'}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {periodConflict.start_date && periodConflict.end_date && (
                  <Button data-testid="turni-open-existing-period" onClick={openExistingPeriod}
                    className="bg-brand-green hover:bg-brand-green-hover text-white">
                    Apri periodo esistente
                  </Button>
                )}
                <Button data-testid="turni-cancel-period-conflict" variant="outline"
                  onClick={() => setPeriodConflict(null)}>
                  Annulla
                </Button>
              </div>
            </>
          ) : (
            <>
            <p className="text-stone-600 text-sm mb-4">Nessun periodo per questa settimana.</p>
            {canCreate && (
            <Button data-testid="turni-create-period" onClick={createPeriod} disabled={actionBusy === 'create'}
              className="bg-brand-green hover:bg-brand-green-hover text-white gap-2">
               {actionBusy === 'create' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Crea periodo
            </Button>
            )}
            </>
          )}
        </div>
      )}

      {/* Grid (desktop + mobile share the same scrollable table, mobile horizontal scroll) */}
         {ws && visibleEmployees.length > 0 && (
          <div ref={turniGridRef} className="scroll-mt-4 rounded-xl md:rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
           <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-[10px] font-bold tracking-wider ${
             tableIsDraft
               ? 'border-amber-200 bg-amber-50 text-amber-900'
               : 'border-emerald-200 bg-emerald-50 text-emerald-900'
           }`} data-testid="turni-table-version-state">
              <span>{tableIsDraft ? 'BOZZA — MODIFICABILE' : 'VERSIONE PUBBLICATA'}</span>
             <span className="font-medium tracking-normal">
               {tableIsDraft
                 ? (hasPublishedVersion ? `In preparazione della versione ${draftVersion}` : 'Nessuna pubblicazione ancora effettuata')
                  : `Versione ${tableVersion}${isReadOnlyVersion ? ' · SOLA LETTURA' : ''}`}
             </span>
           </div>
          <div className="overflow-x-auto flex-1 min-h-0">
             <table className="min-w-[900px] w-full text-xs border-collapse" data-testid="turni-grid">
              <thead>
                <tr>
                   <th rowSpan={3} className="sticky left-0 z-20 bg-white border-r border-b border-stone-200 text-left px-2 md:px-3 py-1.5 md:py-2 w-[112px] min-w-[112px] md:min-w-[140px] align-top">
                     <div className="h-full min-h-[94px] flex flex-col justify-between text-[9px] md:text-[10px] uppercase tracking-widest text-stone-500 font-bold">
                       <span>Dipendente</span>
                       <span>Servizio</span>
                       <span>Copertura</span>
                     </div>
                  </th>
                  {WEEK_LABELS_FULL.map((lab, i) => {
                    const d = addDays(weekStart, i);
                    return (
                      <th key={i} colSpan={services.length}
                        className="border-b border-l border-stone-200 text-center px-1 py-1 md:px-2 md:py-2 bg-white">
                        <div className="text-[11px] md:text-xs font-bold text-brand-navy tracking-wide leading-tight">{lab} {dayNum(d)}</div>
                      </th>
                    );
                  })}
                </tr>
                <tr>
                  {WEEK_LABELS_FULL.map((_, i) => (
                    services.map(svc => (
                      <th key={`s-${i}-${svc.id}`}
                        className={`border-b border-l border-stone-100 text-center px-1 py-0.5 md:py-1 text-[10px] md:text-[11px] font-semibold w-[62px] md:w-auto ${
                          svc.code === 'CENA' ? 'bg-stone-50 text-stone-700' : 'bg-amber-50 text-amber-800'
                        }`}>
                        {svc.name}
                      </th>
                    ))
                  ))}
                </tr>
                <tr>
                  {WEEK_LABELS_FULL.map((_, i) => {
                    const d = addDays(weekStart, i);
                    return services.map(svc => {
                      const cov = coverageByCell[`${iso(d)}::${svc.id}`];
                      return (
                        <th key={`c-${i}-${svc.id}`} className="border-b border-l border-stone-100 text-center px-0.5 py-0.5 md:px-1 md:py-1">
                          <CoveragePill cov={cov} />
                        </th>
                      );
                    });
                  })}
                </tr>
              </thead>
              <tbody>
                 {visibleEmployees.map(emp => (
                  <tr key={emp.employee_id} className="border-t border-stone-100">
                    <td className="sticky left-0 bg-white border-r border-stone-200 px-2 md:px-3 py-1.5 md:py-2.5 align-middle w-[112px] md:min-w-[120px]">
                      <div className="font-bold text-stone-900 text-[11.5px] md:text-[12px] leading-tight uppercase truncate">{emp.display_name}</div>
                      <div className="text-[10px] text-stone-500 mt-0.5">{(weeklyHours[emp.employee_id] || 0).toFixed(0)} h</div>
                    </td>
                    {WEEK_LABELS_FULL.map((_, dIdx) => {
                      const d = addDays(weekStart, dIdx);
                      return services.map(svc => (
                        <CellRenderer key={`${dIdx}-${svc.id}`}
                          emp={emp} day={d} svc={svc}
                          shifts={shiftsByCell[`${emp.employee_id}::${iso(d)}::${svc.id}`] || []}
                            canCreate={canCreate && !isReadOnlyVersion}
                            canOpenDetails={canReplace && period?.status === 'DRAFT' && !isReadOnlyVersion}
                           isEditing={editingWeek} onCreate={openCreate} onEdit={openEdit}
                        />
                      ));
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

       {ws && (
          <section className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm md:rounded-2xl md:p-4"
            aria-labelledby="turni-period-notes-title" data-testid="turni-period-notes">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 id="turni-period-notes-title" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-navy">
                  <StickyNote className="h-4 w-4 text-brand-green" aria-hidden="true" /> Note del periodo
                </h2>
                <p className="mt-0.5 text-[11px] text-stone-500">Note dei turni visibili nella settimana e nello scope corrente.</p>
              </div>
              <span className="shrink-0 rounded-full bg-stone-100 px-2 py-1 text-[10px] font-semibold text-stone-600">
                {periodNotes.length} {periodNotes.length === 1 ? 'nota' : 'note'}
              </span>
            </div>
            {periodNotes.length === 0 ? (
              <p className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4 text-center text-sm text-stone-500"
                data-testid="turni-period-notes-empty">
                Nessuna nota nei turni visualizzati.
              </p>
            ) : (
              <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3" data-testid="turni-period-notes-list">
                {periodNotes.map(shift => (
                  <article key={shift.id} className="rounded-xl border border-stone-200 bg-stone-50/60 p-3"
                    data-testid={`turni-period-note-${shift.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-brand-navy">{shift.employee_name}</p>
                        <p className="mt-0.5 text-xs capitalize text-stone-500">
                          {dateLabelIT(shift.shift_date)} · {shift.service_name}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-semibold text-stone-500">
                        {fmtHM(shift.start_time)}–{fmtHM(shift.end_time)}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm text-stone-700">{shift.note}</p>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {ws && (
         <div className="hidden md:block">
           <button type="button" data-testid="turni-availability-toggle-desktop"
             onClick={toggleAvailability}
             className="inline-flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border border-blue-300 bg-blue-50 px-4 text-left text-sm font-bold text-blue-800 shadow-sm transition-colors hover:bg-blue-100 md:rounded-2xl">
             <span className="inline-flex items-center gap-2">
               <CalendarIcon className="h-4 w-4 shrink-0 text-blue-700" aria-hidden="true" />
               {availabilityOpen ? 'Chiudi disponibilità' : 'Disponibilità'}
             </span>
             <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${availabilityOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
           </button>
           {availabilityOpen && (
             <ManagerAvailabilitySummary
               ref={availabilityDesktopSummaryRef}
               rows={availabilityRows}
               scopeLabel={scopeLabel}
               period={period}
             />
           )}
         </div>
       )}

       {/* Thin totals bar (mobile ~44px). Riposi = SOT missing → "—" */}
      {ws && employees.length > 0 && (
         <div className="rounded-xl md:rounded-2xl border border-stone-200 bg-white min-h-14 md:h-auto md:p-4 px-2 md:px-3 grid grid-cols-4 items-center gap-0 overflow-hidden shrink-0"
          data-testid="turni-totals">
           <TotalStat label="Ore programmate" value={`${totalProgrammate.toFixed(0)} h`} />
           <TotalStat label="Ore pranzo" value={`${(totalByService[services.find(s => s.code === 'PRANZO')?.id] || 0).toFixed(0)} h`} />
           <TotalStat label="Ore cena" value={`${(totalByService[services.find(s => s.code === 'CENA')?.id] || 0).toFixed(0)} h`} />
           <TotalStat label="Riposi" value={`${restDays}`} />
        </div>
      )}
       {ws && employees.length > 0 && visibleEmployees.length === 0 && (
         <div className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500"
           data-testid="turni-search-empty">
           Nessun dipendente corrisponde alla ricerca.
         </div>
       )}

       {ws && (
         <div className="md:hidden">
           <button type="button" data-testid="turni-availability-toggle"
             onClick={toggleAvailability}
             className="inline-flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border border-blue-300 bg-blue-50 px-4 text-left text-sm font-bold text-blue-800 shadow-sm transition-colors hover:bg-blue-100 md:rounded-2xl">
             <span className="inline-flex items-center gap-2">
               <CalendarIcon className="h-4 w-4 shrink-0 text-blue-700" aria-hidden="true" />
               {availabilityOpen ? 'Chiudi disponibilità' : 'Disponibilità'}
             </span>
             <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${availabilityOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
           </button>
           {availabilityOpen && (
             <ManagerAvailabilitySummary
               ref={availabilityMobileSummaryRef}
               rows={availabilityRows}
               scopeLabel={scopeLabel}
               period={period}
             />
           )}
         </div>
       )}

       {ws && isDesktop && (
          <div className="rounded-xl border border-stone-200 bg-white px-3 py-2.5 md:rounded-2xl md:px-4"
          aria-label="Legenda tipi turno" data-testid="turni-legend">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-2">Legenda</div>
          <div className="flex flex-wrap gap-1.5">
            {SHIFT_TYPES.map(([value, label, meta]) => (
              <span key={value} className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium"
                style={{ backgroundColor: meta.fill, borderColor: meta.border, color: meta.text, textDecoration: meta.strike ? 'line-through' : undefined }}>
                {label}
              </span>
            ))}
          </div>
        </div>
      )}

      </div>{/* /content padding wrapper */}

      {/* Scope drawer */}
      <Sheet open={scopeOpen} onOpenChange={setScopeOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="turni-scope-sheet">
          <SheetHeader><SheetTitle>Scope</SheetTitle></SheetHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Azienda</Label>
              <Select value={companyId || ''} onValueChange={(v) => { setCompanyId(v); setLocationId(null); setDepartmentId(null); }}>
                <SelectTrigger data-testid="turni-select-company" className="mt-1"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Locale</Label>
              <Select value={locationId || ''} onValueChange={(v) => { setLocationId(v); setDepartmentId(null); }} disabled={!companyId}>
                <SelectTrigger data-testid="turni-select-location" className="mt-1"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                <SelectContent>{locationsInCompany.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Reparto</Label>
              <Select value={departmentId || 'all'} onValueChange={(v) => setDepartmentId(v === 'all' ? null : v)} disabled={!locationId}>
                <SelectTrigger data-testid="turni-select-department" className="mt-1"><SelectValue placeholder="Tutti" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i reparti</SelectItem>
                  {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="turni-scope-apply" className="w-full bg-brand-green hover:bg-brand-green-hover text-white"
              onClick={() => setScopeOpen(false)}>Applica</Button>
          </div>
        </SheetContent>
      </Sheet>

      {shiftModal && (
        <ShiftModal data={shiftModal} setData={setShiftModal} services={services}
          canEdit={canEdit} canReplace={canReplace} isDraft={period?.status === 'DRAFT'}
          onSave={saveShift} onCancel={cancelShift}
          onFindReplacementCandidates={openReplacementCandidates}
          onShiftTypeChange={handleShiftTypeChange} />
      )}
      {replacementModal && (
        <ReplacementCandidatesDialog
          data={replacementModal}
          busy={actionBusy === 'replacement'}
          onSelect={(employeeId) => setReplacementModal(current => current ? {
            ...current, selectedEmployeeId: employeeId, error: null,
          } : current)}
          onConfirm={confirmReplacement}
          onClose={() => { if (!actionBusy) setReplacementModal(null); }}
        />
      )}
      {periodModal && (
        <PeriodEditModal data={periodModal} setData={setPeriodModal} busy={actionBusy === 'edit'}
          onSave={savePeriodEdit} onClose={() => setPeriodModal(null)} />
      )}
      {versionsOpen && versions && (
        <VersionsDialog
          versions={versions}
          viewedVersion={viewedVersion}
          onView={(version) => {
            setVersionsOpen(false);
            setViewedVersion(Number(version));
          }}
          onClose={() => setVersionsOpen(false)}
        />
      )}
      {publicationConfirm && (
        <PublicationConfirmationDialog
          version={publicationConfirm.version}
          busy={actionBusy === 'publish'}
          onCancel={() => { if (actionBusy !== 'publish') setPublicationConfirm(null); }}
          onConfirm={publishPeriod}
        />
      )}
      {warningDialog && (
        <WarningConfirmationDialog
          warnings={warningDialog.warnings}
          context={warningDialog.context}
          onCancel={warningDialog.onCancel}
          onConfirm={warningDialog.onConfirm}
        />
      )}
    </div>
  );
}

function PublicationConfirmationDialog({ version, busy, onCancel, onConfirm }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent className="max-w-md p-5" data-testid="turni-publication-confirm">
        <DialogHeader>
          <DialogTitle>Conferma pubblicazione</DialogTitle>
          <DialogDescription>
            Stai per pubblicare la versione {version}. La pubblicazione creerà uno snapshot
            immutabile e manterrà le versioni precedenti nello storico.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
          Dopo la conferma, la versione pubblicata non sarà modificabile direttamente.
          Per ulteriori cambiamenti dovrai aprire una nuova bozza.
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            Torna alla bozza
          </Button>
          <Button type="button" data-testid="turni-publication-confirm-submit"
            disabled={busy} onClick={onConfirm}
            className="bg-brand-navy hover:bg-brand-navy/90 text-white">
            {busy ? 'Pubblicazione…' : `Pubblica versione ${version}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- toolbar button ----------
function ToolbarBtn({ testid, tone, icon: Icon, label, onClick, disabled, compact }) {
  const styles = {
    neutral: 'bg-white border-stone-200 text-stone-800 hover:bg-stone-50',
    danger:  'bg-white border-red-200 text-red-700 hover:bg-red-50',
    soft:    'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100',
    dark:    'bg-brand-navy border-brand-navy text-white hover:opacity-90',
    info:    'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100',
  }[tone] || 'bg-white';
  const size = compact
    ? 'w-full min-w-0 px-2.5 h-11 min-h-11 text-[12px] justify-center'
    : 'px-3 min-h-11 py-2 text-[13px] shrink-0';
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className={`inline-flex items-center gap-1.5 shrink-0 rounded-xl border font-semibold ${size} ${styles} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}>
      <Icon className="w-3.5 h-3.5" /> <span className="truncate">{label}</span>
    </button>
  );
}

// ---------- period editor ----------
function PeriodEditModal({ data, setData, busy, onSave, onClose }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="max-w-md p-5" data-testid="turni-period-modal">
        <DialogHeader>
          <DialogTitle>Modifica periodo</DialogTitle>
          <DialogDescription>
            Le modifiche restano in bozza finché il periodo non viene pubblicato.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div>
            <Label htmlFor="turni-period-start" className="text-xs">Inizio</Label>
            <Input id="turni-period-start" data-testid="turni-period-start" type="date" value={data.start_date}
              onChange={(e) => setData({ ...data, start_date: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="turni-period-end" className="text-xs">Fine</Label>
            <Input id="turni-period-end" data-testid="turni-period-end" type="date" value={data.end_date}
              onChange={(e) => setData({ ...data, end_date: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Annulla</Button>
          <Button type="button" data-testid="turni-period-save" disabled={busy} onClick={onSave}
            className="bg-brand-green hover:bg-brand-green-hover text-white">
            {busy ? 'Salvataggio…' : 'Salva modifiche'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- publication versions ----------
function VersionsDialog({ versions, viewedVersion, onView, onClose }) {
  const items = Array.isArray(versions) ? versions : [];
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm p-5" data-testid="turni-versions-modal">
        <DialogHeader>
          <DialogTitle>Versioni</DialogTitle>
          <DialogDescription>
            Seleziona una versione per visualizzare i suoi turni reali. Le versioni pubblicate sono sola lettura.
          </DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-3 py-6 text-center text-sm text-stone-500"
            data-testid="turni-versions-empty">
            Nessuna versione pubblicata per questo periodo.
          </p>
        ) : (
          <div className="max-h-[55vh] space-y-1 overflow-y-auto pr-1" data-testid="turni-versions-list">
            {items.map((version) => {
              const publishedAt = version.published_at
                ? new Date(version.published_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
                : '—';
              const isLatest = version.version === items[0]?.version;
              return (
                 <div key={version.id}
                   className="flex items-center justify-between gap-3 rounded-lg border border-stone-200 bg-white px-3 py-2">
                   <div className="min-w-0">
                     <div className="flex items-center gap-2">
                       <span className="font-semibold text-stone-900">V{version.version} · {isLatest ? 'Pubblicata' : 'Precedente'}</span>
                       {viewedVersion === version.version && (
                         <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-blue-800">
                           VISUALIZZATA
                         </span>
                       )}
                     </div>
                     <div className="mt-0.5 text-xs text-stone-500">{publishedAt}</div>
                   </div>
                   <Button type="button" variant="outline" size="sm"
                     data-testid={`turni-version-view-${version.version}`}
                     onClick={() => onView(version.version)}>
                     Visualizza
                   </Button>
                </div>
              );
            })}
          </div>
        )}
        <DialogFooter>
          <Button type="button" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReplacementCandidatesDialog({ data, busy, onSelect, onConfirm, onClose }) {
  const shift = data.shift || {};
  const candidates = data.candidates || [];
  const candidateGroups = groupReplacementCandidates(candidates);
  const selected = candidates.find(candidate => candidate.employee_id === data.selectedEmployeeId);
  const serviceName = shift.service_name || 'Servizio';
  const targetEmployee = shift.snapshot_employee_name || 'Dipendente';
  const targetLocation = shift.location_name || 'Locale non specificato';
  const targetDepartment = shift.department_name || 'Reparto non specificato';
  const candidateCount = data.candidateCount ?? candidates.length;
  const candidateCountLabel = `${candidateCount} ${candidateCount === 1 ? 'candidato' : 'candidati'} compatibil${candidateCount === 1 ? 'e' : 'i'}`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="flex max-h-[94vh] max-w-2xl flex-col overflow-hidden p-0 sm:max-h-[92vh]"
        data-testid="turni-replacement-modal">
        <div className="shrink-0 border-b border-stone-200 px-4 pb-3 pt-5 md:px-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-brand-navy">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                <UsersRound className="h-4 w-4 text-brand-green" />
              </span>
              Candidati sostituzione
            </DialogTitle>
            <DialogDescription>
              Scegli manualmente chi sostituirà {targetEmployee}. La verifica finale resta server-side.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 rounded-2xl border border-stone-200 bg-stone-50/80 px-3 py-3 text-sm"
            data-testid="turni-replacement-target">
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-stone-900">
                {targetEmployee} <span className="font-normal text-stone-400">·</span> {targetLocation} <span className="font-normal text-stone-400">·</span> {targetDepartment}
              </p>
              <p className="text-xs text-stone-600">
                {dateLabelIT(shift.shift_date, { weekday: 'short', day: 'numeric', month: 'long' })} <span className="text-stone-400">·</span> {serviceName} <span className="text-stone-400">·</span> {fmtHM(shift.start_time)}–{fmtHM(shift.end_time)}
              </p>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="font-semibold text-brand-navy">{candidateCountLabel}</span>
              <span className="text-stone-400">·</span>
              <span className="text-stone-500">Ordinati per priorità e compatibilità</span>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 md:px-6" data-testid="turni-replacement-scroll">
          {data.error && (
            <div role="alert" data-testid="turni-replacement-error"
              className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{data.error}</span>
            </div>
          )}

          {data.loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-stone-500"
              data-testid="turni-replacement-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Caricamento candidati…
            </div>
          ) : candidates.length === 0 && !data.error ? (
            <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-4 py-8 text-center text-sm text-stone-500"
              data-testid="turni-replacement-empty">
              Nessun candidato compatibile per questo turno.
            </div>
          ) : candidates.length > 0 ? (
            <div className="space-y-4" data-testid="turni-replacement-candidates">
              {candidateGroups.map(group => (
                <section key={group.key} data-testid={`turni-replacement-group-${group.testId}`}>
                  <div className="mb-2 flex items-center justify-between gap-3 px-1">
                    <div className="min-w-0">
                      <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-brand-navy">{group.title}</h3>
                      <p className="mt-0.5 truncate text-[11px] text-stone-500">{group.description}</p>
                    </div>
                    <span className="shrink-0 rounded-full bg-stone-100 px-2 py-1 text-[11px] font-semibold text-stone-600">
                      {group.candidates.length} {group.candidates.length === 1 ? 'candidato' : 'candidati'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {group.candidates.map((candidate, index) => {
                      const isSelected = candidate.employee_id === data.selectedEmployeeId;
                      const candidateAvailability = candidate.availability || {};
                      const availabilityUndeclared = !candidateAvailability.state
                        || candidateAvailability.state === 'NON_INDICATA';
                      const scoreLabel = candidate.score ?? '—';
                      return (
                        <label key={candidate.employee_id}
                          className={`block cursor-pointer rounded-2xl border p-3 transition ${
                            isSelected
                              ? 'border-brand-green bg-emerald-50 ring-2 ring-emerald-100'
                              : 'border-stone-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40'
                          }`}
                          data-testid={`turni-replacement-candidate-${candidate.employee_id}`}>
                          <input type="radio" name="turni-replacement-candidate"
                            value={candidate.employee_id} checked={isSelected}
                            onChange={() => onSelect(candidate.employee_id)}
                            aria-label={`Seleziona ${candidate.employee_name}`}
                            className="sr-only" />
                          <div className="flex items-start gap-3">
                            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                              isSelected ? 'border-brand-green bg-brand-green text-white' : 'border-stone-300 text-transparent'
                            }`} aria-hidden="true">
                              <Check className="h-3.5 w-3.5" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="truncate font-bold text-brand-navy">
                                    {candidate.employee_name}
                                  </p>
                                  <p className="mt-0.5 truncate text-xs text-stone-500">
                                    {candidate.assignment?.job_title || 'Mansione non specificata'} <span className="text-stone-300">·</span> {candidate.assignment?.location_name || 'Locale non specificato'}
                                  </p>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-800">
                                    {isSelected ? 'Selezionato' : `Punteggio ${scoreLabel}`}
                                  </span>
                                  {isSelected && (
                                    <span className="text-[10px] font-semibold text-brand-green">Punteggio {scoreLabel}</span>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs text-stone-600 sm:grid-cols-3">
                                <span className={availabilityUndeclared ? 'font-medium text-amber-700' : ''}>
                                  {availabilityUndeclared ? '🟠 ' : ''}Disponibilità: <strong className="text-stone-800">
                                    {availabilityStateLabel(candidateAvailability.state || 'NON_INDICATA')}
                                  </strong>
                                </span>
                                <span>Carico: <strong className="text-stone-800">{candidate.weekly_hours ?? 0}h → {candidate.projected_weekly_hours ?? 0}h</strong></span>
                                <span>Priorità: <strong className="text-stone-800">{replacementProximityLabel(candidate.organizational_proximity)}</strong></span>
                              </div>
                              {(candidate.advantages?.length > 0 || candidate.concerns?.length > 0 || candidate.score_reasons?.length > 0) && (
                                <details className="mt-2 border-t border-stone-100 pt-2 text-xs" data-testid={`turni-replacement-details-${candidate.employee_id}`}>
                                  <summary
                                    className="cursor-pointer select-none font-semibold text-stone-600 hover:text-brand-navy"
                                    onClick={(event) => event.stopPropagation()}>
                                    Dettagli
                                  </summary>
                                  <div className="mt-2 space-y-1.5">
                                    {candidate.advantages?.length > 0 && (
                                      <p className="text-emerald-800">
                                        <strong>Compatibilità:</strong> {candidate.advantages.join(' · ')}
                                      </p>
                                    )}
                                    {candidate.concerns?.length > 0 && (
                                      <p className="text-amber-800">
                                        <strong>Da considerare:</strong> {candidate.concerns.join(' · ')}
                                      </p>
                                    )}
                                    {candidate.score_reasons?.length > 0 && (
                                      <div className="flex flex-wrap gap-1.5">
                                        {candidate.score_reasons.map(reason => (
                                          <span key={reason.code}
                                            className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] text-stone-600">
                                            {REPLACEMENT_REASON_LABELS[reason.code] || reason.code.replaceAll('_', ' ')}: +{reason.points}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-stone-200 bg-white px-4 py-3 md:px-6">
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>Annulla</Button>
          <Button type="button" data-testid="turni-replacement-confirm" disabled={busy || data.loading || !selected}
            onClick={onConfirm} className="bg-brand-green text-white hover:bg-brand-green-hover">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Conferma…</> : 'Conferma sostituzione'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- coverage pill ----------
function CoveragePill({ cov }) {
  if (!cov) return <span className="inline-flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 bg-stone-50 text-stone-400 border border-stone-200">—</span>;
  const state = cov.state;
  const cls =
    state === 'SOTTO_MINIMO' || state === 'SOPRA_MAX' ? 'bg-red-50 text-red-700 border-red-200' :
    state === 'IDEALE'                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    'bg-amber-50 text-amber-700 border-amber-200';
  const labels = { SOTTO_MINIMO: 'Sotto minimo', TRA_MIN_IDEALE: 'Verso ideale',
    IDEALE: 'Ideale', TRA_IDEALE_MAX: 'Sopra ideale', SOPRA_MAX: 'Sopra massimo' };
  return (
    <span title={labels[state] || 'Non configurata'}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"/>
      {cov.actual}/{cov.ideal || cov.min}
    </span>
  );
}

// ---------- cell ----------
function CellRenderer({
  emp, day, svc, shifts, canCreate, canOpenDetails, isEditing, onCreate, onEdit,
}) {
  const active = shifts.find(s => s.status === 'ACTIVE');
  const cancelled = !active && shifts.find(s => s.status === 'CANCELLED');
  const cellBg = svc.code === 'CENA' ? 'bg-stone-50/40' : 'bg-amber-50/30';
  const dayBorder = svc.code === 'PRANZO' ? 'border-l-2 border-l-stone-300' : 'border-l border-stone-100';
  const cellBase = `${dayBorder} border-t border-stone-100 align-top p-0.5 md:p-1 w-[60px] md:min-w-[68px] ${cellBg}`;

  if (active) {
    const typeMeta = SHIFT_TYPE_META[active.shift_type || 'LAVORO'] || SHIFT_TYPE_META.LAVORO;
    const showsHours = hasShiftTimeRange(active);
    const showTypeLabel = (active.shift_type || 'LAVORO') !== 'LAVORO';
    return (
      <td className={cellBase}>
         <button data-testid={`turni-shift-${active.id}`}
           onClick={() => (isEditing || canOpenDetails) && onEdit(active, emp)}
          className="w-full text-left rounded-md md:rounded-lg border px-1.5 py-1.5 md:px-2 md:py-2 leading-tight hover:brightness-105 transition shadow-sm"
          style={{ backgroundColor: typeMeta.fill, borderColor: typeMeta.border, color: typeMeta.text }}>
          {showTypeLabel && <div className="text-[10px] md:text-[11px] font-bold">{typeMeta.label}</div>}
          {showsHours && <>
            <div className={`flex items-center justify-between gap-1 text-[11px] md:text-xs font-bold ${showTypeLabel ? 'mt-1' : ''}`}>
              <span>{fmtHM(active.start_time)}</span>
              {String(active.notes || '').trim() && (
                <span title="Turno con nota" aria-label="Turno con nota" className="shrink-0">
                  <StickyNote className="h-3 w-3" aria-hidden="true" />
                </span>
              )}
            </div>
            <div className="text-[11px] md:text-xs font-bold mt-1">{fmtHM(active.end_time)}</div>
          </>}
        </button>
      </td>
    );
  }
  if (cancelled) {
    return (
      <td className={cellBase}>
        <button data-testid={`turni-shift-${cancelled.id}`} onClick={() => isEditing && onEdit(cancelled, emp)}
          className="w-full rounded-md md:rounded-lg border px-1 py-1 md:py-1.5 text-[9px] md:text-[10px] font-medium text-center"
          style={{ backgroundColor: turniVisualTokens.ANNULLATO.fill, borderColor: turniVisualTokens.ANNULLATO.border, color: turniVisualTokens.ANNULLATO.text, textDecoration: 'line-through' }}>
          {turniVisualTokens.ANNULLATO.label}
        </button>
      </td>
    );
  }
  return (
    <td className={cellBase}>
      {canCreate && isEditing ? (
        <button data-testid={`turni-cell-empty-${emp.employee_id}-${iso(day)}-${svc.code}`}
          onClick={() => onCreate(emp, day, svc)}
          className="w-full h-full min-h-[38px] md:min-h-[46px] rounded-md md:rounded-lg border border-transparent hover:border-brand-green/40 text-stone-300 hover:text-brand-green transition-colors">
          <Plus className="w-3 h-3 mx-auto" />
        </button>
      ) : <div className="min-h-[38px] md:min-h-[46px]" />}
    </td>
  );
}

function WarningConfirmationDialog({ warnings, context = [], onCancel, onConfirm }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(reason.trim() || null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <DialogContent className="max-w-lg p-5" data-testid="turni-warning-confirmation">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="h-5 w-5 text-amber-600" /> Conferma avviso operativo
          </DialogTitle>
          <DialogDescription>
            L’operazione può continuare, ma presenta questi conflitti reali. Nessun turno viene modificato senza la tua conferma.
          </DialogDescription>
        </DialogHeader>
        {context.length > 0 && (
          <div className="space-y-2 rounded-xl border border-stone-200 bg-stone-50 p-3" data-testid="turni-warning-context">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-500">Turno previsto</p>
            <div className="max-h-36 space-y-2 overflow-y-auto">
              {context.map((item, index) => (
                <div key={`${item.id || item.date || 'context'}-${index}`} className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <span className="text-stone-500">Dipendente</span><strong className="text-right text-stone-900">{item.employeeName}</strong>
                  <span className="text-stone-500">Data · servizio</span><strong className="text-right capitalize text-stone-900">{item.date ? `${dateLabelIT(item.date)} · ${item.service}` : item.service}</strong>
                  <span className="text-stone-500">Orario previsto</span><strong className="text-right text-stone-900">{item.shift}</strong>
                  <span className="text-stone-500">Disponibilità dichiarata</span><span className="text-right text-stone-700">{item.availability}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <ul className="max-h-48 space-y-2 overflow-y-auto rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950"
          data-testid="turni-warning-list">
          {warnings.map((warning, index) => (
            <li key={`${warning.code}-${warning.shift_id || 'period'}-${index}`}>
              <span className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
                <span><strong className="font-semibold">Motivo del conflitto:</strong> {warning.message}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="space-y-1.5">
          <Label htmlFor="turni-warning-reason">Motivazione (facoltativa)</Label>
          <Textarea id="turni-warning-reason" data-testid="turni-warning-reason"
            value={reason} onChange={(event) => setReason(event.target.value)}
            placeholder="Perché confermi questo avviso?" rows={3} maxLength={1000} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Torna alla modifica</Button>
          <Button type="button" data-testid="turni-warning-confirm" disabled={busy} onClick={confirm}
            className="bg-brand-green text-white hover:bg-brand-green-hover">
            {busy ? 'Conferma…' : 'Conferma avviso'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ManagerAvailabilitySummary = React.forwardRef(function ManagerAvailabilitySummary(
  { rows, scopeLabel, period }, ref,
) {
  return (
    <section ref={ref} className="scroll-mt-4 rounded-xl border border-blue-200 bg-white p-3 shadow-sm md:rounded-2xl md:p-4"
      aria-labelledby="turni-availability-summary-title" data-testid="turni-availability-summary">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 id="turni-availability-summary-title" className="text-xs font-bold uppercase tracking-widest text-brand-navy">
            Disponibilità dichiarate
          </h2>
          <p className="mt-0.5 text-[11px] text-stone-500">
            {scopeLabel} · {period ? `${dateLabelIT(period.start_date, { day: 'numeric', month: 'short' })} – ${dateLabelIT(period.end_date, { day: 'numeric', month: 'short' })}` : 'Periodo corrente'}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-800">
          {rows.length} {rows.length === 1 ? 'riga' : 'righe'}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
          Nessuna disponibilità dichiarata per questo periodo e scope.
        </div>
      ) : (
        <div className="grid max-h-[34rem] gap-2 overflow-y-auto md:grid-cols-2 xl:grid-cols-3" data-testid="turni-availability-list">
          {rows.map((item, index) => (
            <article key={`${item.employee_id}-${item.shift_date}-${item.service_id || 'all'}-${index}`}
              className="rounded-xl border border-stone-200 bg-stone-50/50 p-3" data-testid={`turni-availability-item-${index}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-brand-navy">{item.employee_name}</p>
                  <p className="mt-0.5 text-xs capitalize text-stone-500">
                    {dateLabelIT(item.shift_date)} · {item.service_name}
                  </p>
                </div>
                <span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold text-emerald-800">
                  {availabilityStateLabel(item.state)}
                </span>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-stone-500">Servizio</dt>
                <dd className="text-right font-medium text-stone-800">{item.service_name}</dd>
                <dt className="text-stone-500">Fascia Dal–Alle</dt>
                <dd className="text-right font-medium text-stone-800">
                  {item.start_time && item.end_time ? `${fmtHM(item.start_time)}–${fmtHM(item.end_time)}` : 'Non specificata'}
                </dd>
                <dt className="text-stone-500">Nota</dt>
                <dd className="truncate text-right text-stone-700" title={item.notes || undefined}>{item.notes || 'Non specificata'}</dd>
              </dl>
            </article>
          ))}
        </div>
      )}
    </section>
  );
});

// ---------- totals bar item ----------
function TotalStat({ label, value }) {
  return (
    <div className="min-w-0 px-2 md:px-3 border-r last:border-r-0 border-stone-100">
      <div className="text-[8px] md:text-[10px] uppercase tracking-wider text-stone-500 font-semibold md:font-bold md:tracking-widest md:mb-0.5 truncate">{label}</div>
      <div className="text-[13px] md:text-lg font-display font-semibold text-brand-navy leading-none">{value}</div>
    </div>
  );
}

// ---------- shift modal ----------
function ShiftModal({
  data, setData, services, canEdit, canReplace, isDraft, onSave, onCancel,
  onFindReplacementCandidates, onShiftTypeChange,
}) {
  const isEdit = data.mode === 'edit';
  const svc = services.find(x => x.id === data.service_id);
  const typeMeta = SHIFT_TYPE_META[data.shift_type || 'LAVORO'] || SHIFT_TYPE_META.LAVORO;
  const showsHours = data.mode === 'create'
    ? shiftHasTimeFields(data.shift_type)
    : hasShiftTimeRange(data);
  const legDuration = (start, end, pause) => {
    const [sh, sm] = (start || '00:00').split(':').map(Number);
    const [eh, em] = (end || '00:00').split(':').map(Number);
    return Math.max(0, (eh * 60 + em) - (sh * 60 + sm) - (Number(pause) || 0));
  };
  // Live duration (hours + minutes), pause deducted
  const durMin = data.double_shift
    ? legDuration(data.pranzo_start, data.pranzo_end, data.pranzo_break)
      + legDuration(data.cena_start, data.cena_end, data.cena_break)
    : legDuration(data.start_time, data.end_time, data.break_minutes);
  const durH = Math.floor(durMin / 60), durM = durMin % 60;
  // Format shift_date for header, e.g. "Lunedì 17 Agosto"
  const [yy, mm, dd] = (data.shift_date || '').split('-').map(Number);
  const _d = yy ? new Date(yy, mm - 1, dd) : null;
  const dayLabelIT = _d ? _d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) : data.shift_date;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) setData(null); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto pb-24 md:pb-6 p-4 md:p-6" data-testid="turni-shift-modal">
        <DialogHeader className="space-y-1">
          <div className="text-[10px] uppercase tracking-widest text-brand-green font-bold">
            {(svc?.name || '').toUpperCase()} · DETTAGLIO TURNO
          </div>
          <DialogTitle className="text-lg">{isEdit ? 'Modifica turno' : 'Nuovo turno'}</DialogTitle>
          <DialogDescription className="text-xs space-y-0.5">
            <span className="block"><span className="text-stone-400">Dipendente</span> · <span className="text-stone-900 font-medium">{data.employee_name}</span></span>
            <span className="block"><span className="text-stone-400">Data</span> · <span className="text-stone-900 font-medium capitalize">{dayLabelIT}</span></span>
            <span className="block"><span className="text-stone-400">Fascia</span> · <span className="text-stone-900 font-medium">{svc?.name}</span></span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 text-sm mt-3">
          {data.assignments.length > 1 && (
            <div>
              <Label className="text-xs">Assignment</Label>
               <Select value={data.assignment_id} onValueChange={(v) => setData({ ...data, assignment_id: v })} disabled={!canEdit}>
                <SelectTrigger data-testid="turni-modal-assignment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {data.assignments.map(a => (
                    <SelectItem key={a.assignment_id} value={a.assignment_id}>
                      {a.department_name || '—'} · {a.mansione || '—'}{a.is_primary ? ' · principale' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs">Servizio</Label>
             <Select value={data.service_id} onValueChange={(v) => setData({ ...data, service_id: v })} disabled={!canEdit}>
              <SelectTrigger data-testid="turni-modal-service"><SelectValue /></SelectTrigger>
              <SelectContent>{services.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
           <div>
             <Label className="text-xs" htmlFor="turni-modal-shift-type">Tipo turno</Label>
             <select id="turni-modal-shift-type" data-testid="turni-modal-shift-type"
               value={data.shift_type || 'LAVORO'}
                onChange={(e) => onShiftTypeChange(e.target.value)}
                disabled={!canEdit}
                className="mt-1 flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                style={{ backgroundColor: typeMeta.fill, borderColor: typeMeta.border, color: typeMeta.text }}>
               {SHIFT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
             </select>
           </div>
          <div>
            <Label className="text-xs">Data</Label>
             <Input type="date" data-testid="turni-modal-date" value={data.shift_date} disabled={!canEdit}
              onChange={(e) => setData({ ...data, shift_date: e.target.value })} />
          </div>
            {showsHours && !data.double_shift && <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <QuickTimeField id="turni-modal-start" testId="turni-modal-start" label="Inizio"
                value={data.start_time} disabled={!canEdit}
                onChange={(value) => setData({ ...data, start_time: value })} />
              <QuickTimeField id="turni-modal-end" testId="turni-modal-end" label="Fine"
                value={data.end_time} disabled={!canEdit}
                onChange={(value) => setData({ ...data, end_time: value })} />
              <QuickBreakField id="turni-modal-break" testId="turni-modal-break" label="Pausa (min)"
                value={data.break_minutes} disabled={!canEdit}
                onChange={(value) => setData({ ...data, break_minutes: value })} />
           </div>}
           {showsHours && data.double_shift && <div className="space-y-2 rounded-xl border border-stone-200 bg-stone-50 p-3">
             <div className="text-xs font-bold uppercase tracking-widest text-stone-500">Orari distinti</div>
             <div className="grid grid-cols-2 gap-2">
               <div className="col-span-2 text-xs font-semibold text-amber-900">Pranzo</div>
                 <QuickTimeField id="turni-modal-pranzo-start" testId="turni-modal-pranzo-start" label="Inizio Pranzo"
                  value={data.pranzo_start} disabled={!canEdit}
                  onChange={(value) => setData({ ...data, pranzo_start: value })} />
                 <QuickTimeField id="turni-modal-pranzo-end" testId="turni-modal-pranzo-end" label="Fine Pranzo"
                  value={data.pranzo_end} disabled={!canEdit}
                  onChange={(value) => setData({ ...data, pranzo_end: value })} />
                 <div className="col-span-2">
                   <QuickBreakField id="turni-modal-pranzo-break" testId="turni-modal-pranzo-break" label="Pausa Pranzo (min)"
                    value={data.pranzo_break} disabled={!canEdit}
                    onChange={(value) => setData({ ...data, pranzo_break: value })} />
                 </div>
               <div className="col-span-2 text-xs font-semibold text-stone-800">Cena</div>
                 <QuickTimeField id="turni-modal-cena-start" testId="turni-modal-cena-start" label="Inizio Cena"
                  value={data.cena_start} disabled={!canEdit}
                  onChange={(value) => setData({ ...data, cena_start: value })} />
                 <QuickTimeField id="turni-modal-cena-end" testId="turni-modal-cena-end" label="Fine Cena"
                  value={data.cena_end} disabled={!canEdit}
                  onChange={(value) => setData({ ...data, cena_end: value })} />
                 <div className="col-span-2">
                   <QuickBreakField id="turni-modal-cena-break" testId="turni-modal-cena-break" label="Pausa Cena (min)"
                    value={data.cena_break} disabled={!canEdit}
                    onChange={(value) => setData({ ...data, cena_break: value })} />
                 </div>
             </div>
           </div>}
           {showsHours && <div className="rounded-xl bg-stone-50 border border-stone-200 px-3 py-2 flex items-center justify-between">
             <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">Totale turno</span>
             <span className="font-display font-semibold text-brand-navy text-lg" data-testid="turni-modal-duration">
               {durH} h {durM > 0 ? `${durM} min` : ''}
             </span>
           </div>}
           {!isEdit && services.length >= 2 && showsHours && (
            <label className="flex items-center gap-2 text-sm" data-testid="turni-modal-double-shift-label">
              <input type="checkbox" data-testid="turni-modal-double-shift"
                checked={!!data.double_shift}
                 onChange={(e) => setData({
                   ...data, double_shift: e.target.checked,
                   ...(e.target.checked ? { shift_type: 'LAVORO' } : {}),
                 })}
                className="w-4 h-4 accent-brand-green" />
              Doppio turno · Pranzo + Cena
            </label>
          )}
          <div>
            <Label className="text-xs">Nota</Label>
             <Textarea data-testid="turni-modal-notes" value={data.notes} rows={2} disabled={!canEdit}
              onChange={(e) => setData({ ...data, notes: e.target.value })} />
          </div>
           {isEdit && canReplace && isDraft && data.status === 'ACTIVE' && (
             <div className="rounded-xl border border-blue-200 bg-blue-50 p-3" data-testid="turni-replacement-action">
               <p className="text-xs text-blue-800">
                 Cerca una persona compatibile e conferma la sostituzione senza modificare manualmente il turno originale.
               </p>
               <Button type="button" variant="outline" data-testid="turni-replacement-open"
                 onClick={() => onFindReplacementCandidates(data)}
                 className="mt-2 w-full border-blue-300 bg-white text-blue-800 hover:bg-blue-100">
                 <UsersRound className="mr-1.5 h-4 w-4" /> Candidati sostituzione
               </Button>
             </div>
           )}
        </div>
        <DialogFooter className="gap-2 sticky bottom-0 bg-white pt-3 border-t border-stone-100 -mx-6 px-6">
          {isEdit && canEdit && (
            <Button variant="destructive" data-testid="turni-modal-delete" onClick={onCancel}>
              <Trash2 className="w-4 h-4 mr-1" /> Annulla turno
            </Button>
          )}
          <Button variant="ghost" onClick={() => setData(null)}>Chiudi</Button>
           <Button data-testid="turni-modal-save" onClick={onSave} disabled={!canEdit}
             className="bg-brand-green hover:bg-brand-green-hover text-white">
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
